/**
 * Pattern Matcher
 *
 * Matches tokenized input against language patterns to extract semantic roles.
 * This is the core algorithm for multilingual parsing.
 */

import type {
  LanguagePattern,
  PatternToken,
  PatternMatchResult,
  ReferenceValue,
  SemanticRole,
  SemanticValue,
  TokenStream,
  LanguageToken,
} from '../types';
import {
  createSelector,
  createLiteral,
  createReference,
  createPropertyPath,
  isValidReference,
  withPosition,
} from '../types';
import { stripOptionalDiacritics } from '@lokascript/framework';
import { isTypeCompatible } from './utils/type-validation';
import { ROLE_MARKER_CONCEPTS } from './utils/marker-resolution';
import { commandSchemas, type CommandSchema } from '../generators/command-schemas';
import { getPossessiveReference } from './utils/possessive-keywords';
import {
  COMMAND_ACTION_KEYWORDS,
  isOfPossessiveMarker,
  joinExpressionTokens,
  matchPositionalRun,
  matchQueryScope,
  translatePropertyName,
  translateConnective,
  expressionWordOf,
  isKnownPropertySurface,
  isConversionTypeName,
  ambiguousSenseOf,
  ofChainEnd,
  endWordIsValue,
  endWordPlace,
} from './utils/expression-lexicon';
import {
  BINARY_PHRASES,
  POSTFIX_PHRASES,
  TYPE_CHECK_PHRASES,
} from '../ast-builder/expression-parser/parser';
import {
  type ExtentContext,
  longestWholeRun,
  possessiveLinkEnds,
  tokensAdjacent,
  valueTailEnds,
} from './utils/value-extent';
import { COMPARISON_OPERATORS, RUN_OPERATORS } from './utils/operators';
import {
  SlotContext,
  articleIsVariable,
  keywordIsVariable,
  maySkipVerbSlot,
  namesCommand,
  neverAValue,
  endWordIsVariable,
  notWordIsVariable,
  particleIsOperand,
  particleIsValue,
  verbEndsCountSlot,
  verbSkipsOptionalSlot,
} from './value-reading';
import type { LanguageProfile } from '../generators/profiles/types';
import { tryGetProfile } from '../registry';
import { isAtEndConnective } from '../patterns/put';
import { isCuratedEndKeyword } from './end-keywords';
import { foldNakedNamedArgsRaw } from './naked-args-fold';
import { tokenValue } from './token-value';
import type { ConfidenceModel } from './confidence-model';
import { defaultConfidenceModel } from './confidence-model';

/**
 * Body-bearing command keywords that front their own block (`bareKeyword` schemas:
 * `live`/`eventsource`/`socket`/`worker`/`intercept`). The keyword leads the
 * construct (`live put … end`, `socket Name url on message … end`) and is NEVER an
 * event name, so it must not anchor a bare-event pattern at Stage 1 — see
 * tokenLooksLikeEvent. Derived from the schemas so a new bareKeyword block is
 * covered automatically.
 */
const BAREKEYWORD_BLOCK_ACTIONS: ReadonlySet<string> = new Set(
  Object.values(commandSchemas)
    .filter(s => s.bareKeyword === true)
    .map(s => s.action)
);

// =============================================================================
// Pattern Matcher
// =============================================================================

/**
 * A hyperscript SIGIL property — `*background-color` (style) or `@aria-expanded`
 * (attribute). Both tokenize as `selector`, and neither can ever be the OWNER of
 * a possessive, which is what tells an owner-first clitic surface
 * (`#themeの*background-color`) apart from the property-first "of" surface the
 * i18n transformer emitted (`*background-color ของ #theme`).
 */
function isSigilProperty(value: string): boolean {
  return value.startsWith('*') || value.startsWith('@');
}

export class PatternMatcher {
  /** Current language profile for the pattern being matched */
  private currentProfile: LanguageProfile | undefined;
  /**
   * Command of the pattern currently being matched. Used to scope the
   * event-anchor guard to event-HANDLER patterns (`on`) — the `event` role on
   * `send`/`trigger` command patterns is a payload that CAN be a string literal
   * (`send "hello"`) or identifier (`trigger refresh`), and must not be gated.
   */
  private currentPatternCommand: string | undefined;
  /**
   * The command whose roles the current pattern's slots belong to. Equal to
   * `currentPatternCommand` except for the fused `<cmd>-event-*` handler
   * patterns, which are `command: 'on'` and name the wrapped command in
   * `extraction.action.value` — their `patient` slot is `<cmd>`'s patient.
   */
  private currentRoleCommand: string | undefined;
  /**
   * The pattern tokens still to match after the one being matched, through
   * every enclosing group: a value stops at any marker the pattern still owes.
   */
  private patternRest: PatternToken[] = [];
  /**
   * Whether the role slot being matched directly follows a literal the pattern
   * matched: its marker (`[por {quantity}]`), or the command's verb.
   */
  private slotAfterLiteral = false;
  /** `patternTokenWouldMatch`, bound once for the SlotContexts the captures build. */
  private readonly wouldMatch = (pt: PatternToken | undefined, token: LanguageToken): boolean =>
    this.patternTokenWouldMatch(pt, token);
  /** Injectable confidence scoring model (Phase 3.3) */
  private readonly confidenceModel: ConfidenceModel;
  /**
   * Selective memoization cache (Phase 6.1).
   * Caches pattern match results keyed by `tokenPosition:patternId`.
   * Cleared before each top-level matchBest() call.
   */
  private matchCache = new Map<string, PatternMatchResult | null>();
  /**
   * Stream index where each role's capture began, for the current
   * `matchPattern` call. Rebuilt per call, written on the same success path as
   * `captured` — see `PatternMatchResult.roleStarts`.
   */
  private roleStarts = new Map<SemanticRole, number>();
  /**
   * A source phrase found in the event HEAD (`from #btn` right after the event,
   * `#btn から` before the next head literal) by the two head-tolerance sites
   * below. Kept OUT of `captured`: a fused pattern's own `[de {source}]` group
   * later in the template would otherwise overwrite it with the body command's
   * source (`al … de #contacts-btn quitar @disabled de yo` lost `#contacts-btn`
   * that way). The parser decides whose it is — see PatternMatchResult.eventSource.
   */
  private eventSource: SemanticValue | undefined;

  constructor(confidenceModel?: ConfidenceModel) {
    this.confidenceModel = confidenceModel ?? defaultConfidenceModel;
  }

  /**
   * Try to match a single pattern against the token stream.
   * Returns the match result or null if no match.
   */
  matchPattern(tokens: TokenStream, pattern: LanguagePattern): PatternMatchResult | null {
    const mark = tokens.mark();
    const captured = new Map<SemanticRole, SemanticValue>();

    // Get language profile for possessive keyword lookup
    this.currentProfile = tryGetProfile(pattern.language);
    this.currentPatternCommand = pattern.command;
    this.currentRoleCommand = pattern.extraction?.action?.value ?? pattern.command;

    // Reset match counters for this pattern
    this.stemMatchCount = 0;
    this.totalKeywordMatches = 0;
    this.roleStarts = new Map();
    this.eventSource = undefined;

    this.patternRest = [];
    const success = this.matchTokenSequence(tokens, pattern.template.tokens, captured);

    if (!success) {
      tokens.reset(mark);
      return null;
    }

    // Calculate confidence BEFORE applying defaults
    // This ensures defaulted roles don't artificially inflate confidence
    const confidence = this.confidenceModel.calculate({
      pattern,
      captured,
      stemMatchCount: this.stemMatchCount,
      totalKeywordMatches: this.totalKeywordMatches,
    });

    // Apply extraction rules to fill in any missing roles with defaults
    this.applyExtractionRules(pattern, captured);

    return {
      pattern,
      captured,
      consumedTokens: tokens.position() - mark.position,
      confidence,
      roleStarts: this.roleStarts,
      ...(this.eventSource ? { eventSource: this.eventSource } : {}),
    };
  }

  /**
   * Try to match multiple patterns, return the best match.
   */
  matchBest(tokens: TokenStream, patterns: LanguagePattern[]): PatternMatchResult | null {
    // Clear memoization cache for this matching round (Phase 6.1)
    this.matchCache.clear();

    const matches: PatternMatchResult[] = [];
    const startPos = tokens.position();

    for (const pattern of patterns) {
      const mark = tokens.mark();

      // Check memoization cache (Phase 6.1)
      const cacheKey = `${startPos}:${pattern.id}`;
      let result: PatternMatchResult | null;

      if (this.matchCache.has(cacheKey)) {
        result = this.matchCache.get(cacheKey)!;
      } else {
        result = this.matchPattern(tokens, pattern);
        this.matchCache.set(cacheKey, result);
      }

      if (result) {
        matches.push(result);
      }

      tokens.reset(mark);
    }

    if (matches.length === 0) {
      return null;
    }

    // Sort by confidence and priority
    matches.sort((a, b) => {
      // First by priority
      const priorityDiff = b.pattern.priority - a.pattern.priority;
      if (priorityDiff !== 0) return priorityDiff;

      // Then by confidence
      return b.confidence - a.confidence;
    });

    // Re-consume tokens for the best match
    const best = matches[0];
    this.matchPattern(tokens, best.pattern);

    return best;
  }

  /**
   * Match a sequence of pattern tokens against the token stream.
   */
  private matchTokenSequence(
    tokens: TokenStream,
    patternTokens: PatternToken[],
    captured: Map<SemanticRole, SemanticValue>,
    // What follows the sequence itself — a GROUP's last inner token must see the
    // token after the group as its `next` (the trailing-slot verb guard in
    // matchRoleToken keys on nextPatternToken === undefined meaning "pattern
    // truly ends here", and a mid-pattern single-token group is not the end).
    nextAfterSequence?: PatternToken
  ): boolean {
    // Skip leading conjunctions for Arabic (proclitics: و, ف, ول, وب, etc.)
    // BUT NOT if the pattern explicitly expects a conjunction (proclitic patterns)
    const firstPatternToken = patternTokens[0];
    const patternExpectsConjunction =
      firstPatternToken?.type === 'literal' &&
      (firstPatternToken.value === 'and' ||
        firstPatternToken.value === 'then' ||
        firstPatternToken.alternatives?.includes('and') ||
        firstPatternToken.alternatives?.includes('then'));

    if (this.currentProfile?.code === 'ar' && !patternExpectsConjunction) {
      while (tokens.peek()?.kind === 'conjunction') {
        tokens.advance();
      }
    }

    // Source-clause window: after the event role matches, the next 1–2 pattern
    // tokens may be preceded in the stream by a source clause the fused
    // patterns have no slot for — `gdy keydown[…] z .modal jeśli …` (marker
    // BEFORE: pl z / uk з / zh 从) or `keydown[…] de .modal den eğer …`
    // (marker AFTER: ja から / ko 에서 / bn থেকে / hi से / tr den). The window
    // spans 2 pattern tokens because SOV emissions put the clause after the
    // event-marker literal, not directly after the event. See
    // tryConsumeEventSourceClause for the guards.
    let sourceWindow = 0;

    for (let i = 0; i < patternTokens.length; i++) {
      const patternToken = patternTokens[i];

      if (sourceWindow > 0 && patternToken.type === 'literal') {
        this.tryConsumeEventSourceClause(tokens, captured, patternToken);
      }

      // Marker-less optional slot about to eat a command VERB, mid-pattern.
      //
      // The guard in matchRoleToken skips such a slot only when the pattern's
      // very next token is a literal the verb itself satisfies. Where an
      // OPTIONAL group sits between the slot and the verb literal
      // (`[{method}] [using view {manner}] 交換`) that test looks at the
      // group's first token, sees no match, and lets the slot swallow the verb
      // — so `swap #a with #b` rendered SOV (`#a に #b を 交換`) did not parse
      // at all in bn/hi/ja/ko/qu/tr.
      //
      // Widening that test to look THROUGH skippable groups was measured to
      // work and to BREAK the ja goal-reclaim lock: the no-goal transition
      // variant's mid-pattern slot must keep capturing 遷移 and failing, so the
      // verb-anchoring fallback can reclaim goal+duration. Both shapes are
      // structurally identical at the slot, so no per-slot test can separate
      // them.
      //
      // What separates them is the OUTCOME. Skipping the slot lets swap's
      // pattern consume its clause ENTIRELY (`#a に #b を 交換` → end), while
      // the ja no-goal variant completes having eaten only `opacity を 遷移`
      // and STRANDS `0 に 300ms` — the sloppy match the lock exists to prevent.
      // So: speculatively skip, match the rest of THIS pattern, and adopt the
      // skip only when it consumes the whole clause. A stranded tail falls
      // through to the capture-and-fail below, unchanged.
      //
      // Bounded: fires only on an optional marker-less role slot facing a
      // command-verb keyword the next pattern token does not want, and recurses
      // on a strictly shorter token list — never a re-run of other patterns
      // (the abandoned last-resort retry in matchBest, which compounded).
      if (
        maySkipVerbSlot(
          patternToken,
          tokens.peek(),
          patternTokens[i + 1] ?? nextAfterSequence,
          this.wouldMatch
        )
      ) {
        const skipMark = tokens.mark();
        const speculative = new Map(captured);
        if (
          this.matchTokenSequence(
            tokens,
            patternTokens.slice(i + 1),
            speculative,
            nextAfterSequence
          ) &&
          this.atClauseEnd(tokens)
        ) {
          captured.clear();
          for (const [role, value] of speculative) captured.set(role, value);
          return true;
        }
        tokens.reset(skipMark);
      }

      const outerRest = this.patternRest;
      this.patternRest = [...patternTokens.slice(i + 1), ...outerRest];
      this.slotAfterLiteral =
        patternToken.type === 'role' && patternTokens[i - 1]?.type === 'literal';
      const matched = this.matchPatternToken(
        tokens,
        patternToken,
        captured,
        patternTokens[i + 1] ?? nextAfterSequence
      );
      this.patternRest = outerRest;

      sourceWindow =
        patternToken.type === 'role' && patternToken.role === 'event'
          ? 2
          : Math.max(0, sourceWindow - 1);

      if (!matched) {
        // If token is optional, continue
        if (this.isOptional(patternToken)) {
          continue;
        }
        return false;
      }
    }

    return true;
  }

  /**
   * Consume a source clause (`<marker> <selector>` or `<selector> <marker>`,
   * per the profile's source-marker position) sitting between the event head
   * and the next pattern literal. Fired only inside the post-event window and
   * only when the upcoming literal does NOT match the stream position — a
   * pattern that explicitly handles the marker (`bei {event} von {source}`)
   * is left alone, and a failing consumption is harmless because matchPattern
   * resets the stream on failure.
   */
  private tryConsumeEventSourceClause(
    tokens: TokenStream,
    captured: Map<SemanticRole, SemanticValue>,
    upcoming: PatternToken & { type: 'literal' }
  ): void {
    if (captured.has('source') || this.eventSource) return;
    const tok0 = tokens.peek();
    const tok1 = tokens.peek(1);
    if (!tok0 || !tok1) return;
    // The pattern explicitly expects this token next — leave it alone.
    if (this.patternTokenWouldMatch(upcoming, tok0)) return;

    const src = this.currentProfile?.roleMarkers?.source;
    const isMarker = (t: LanguageToken): boolean => {
      if (t.kind !== 'particle' && t.kind !== 'keyword') return false;
      if ((t.normalized ?? '').toLowerCase() === 'source') return true;
      if (!src) return false;
      const v = t.value.toLowerCase();
      if (src.primary?.toLowerCase() === v) return true;
      return !!src.alternatives?.some(a => a.toLowerCase() === v);
    };
    const position = src?.position;

    // Prepositional `from .modal` (pl z, uk з, zh 从, de von, sw kutoka).
    if (position !== 'after' && isMarker(tok0)) {
      const v = this.sourceNounValue(tok1);
      if (!v) return;
      tokens.advance();
      tokens.advance();
      this.eventSource = v;
      return;
    }
    // Postpositional `.modal から` (ja から, ko 에서, bn থেকে, hi से, tr den).
    if (position === 'after' && isMarker(tok1)) {
      const v = this.sourceNounValue(tok0);
      if (!v) return;
      tokens.advance();
      tokens.advance();
      this.eventSource = v;
    }
  }

  /**
   * The value of an event-source noun, or undefined when the token cannot be
   * one. A source is a selector (`#btn`, `.modal`) or a reference the profile
   * knows — `window` / `document` / `body` in the language's own word
   * (ウィンドウ, fenster, نافذة) or left in English — never a bare identifier or
   * `me`: a body command's first noun must not be eaten by the head.
   */
  private sourceNounValue(t: LanguageToken): SemanticValue | undefined {
    if (t.kind === 'selector') return this.tokenToSemanticValue(t) ?? undefined;
    if (t.kind !== 'keyword' && t.kind !== 'identifier') return undefined;
    const name = (t.normalized ?? t.value).toLowerCase();
    if (name === 'me') return undefined;
    if (isValidReference(name)) return { type: 'reference', value: name };
    // A plain identifier is a variable or behavior parameter (`from triggerEl`)
    // or the `elsewhere` keyword upstream leaves untranslated — typed exactly
    // as the en parse types it. A KEYWORD that is not a reference is a verb or
    // marker, never a source.
    if (t.kind === 'identifier' && /^[A-Za-z_$][\w$]*$/.test(t.value)) {
      return { type: 'expression', raw: t.value };
    }
    return undefined;
  }

  /**
   * Match a single pattern token against the current position in the stream.
   */
  private matchPatternToken(
    tokens: TokenStream,
    patternToken: PatternToken,
    captured: Map<SemanticRole, SemanticValue>,
    nextPatternToken?: PatternToken
  ): boolean {
    switch (patternToken.type) {
      case 'literal':
        return this.matchLiteralToken(tokens, patternToken);

      case 'role': {
        // Record where this slot's own span begins, but only once it actually
        // captured — an optional role that matched nothing must not claim a
        // position. See `PatternMatchResult.roleStarts`.
        const roleStart = tokens.position();
        const matchedRole = this.matchRoleToken(tokens, patternToken, captured, nextPatternToken);
        if (matchedRole && captured.has(patternToken.role)) {
          this.roleStarts.set(patternToken.role, roleStart);
        }
        return matchedRole;
      }

      case 'group':
        return this.matchGroupToken(tokens, patternToken, captured, nextPatternToken);

      default:
        return false;
    }
  }

  /**
   * Match a literal pattern token (keyword or particle).
   */
  private matchLiteralToken(
    tokens: TokenStream,
    patternToken: PatternToken & { type: 'literal' }
  ): boolean {
    const token = tokens.peek();
    if (!token) return false;

    // Check main value
    const matchType = this.getMatchType(token, patternToken.value);
    if (matchType !== 'none') {
      this.totalKeywordMatches++;
      if (matchType === 'stem') {
        this.stemMatchCount++;
      }
      tokens.advance();
      return true;
    }

    // Check alternatives
    if (patternToken.alternatives) {
      for (const alt of patternToken.alternatives) {
        const altMatchType = this.getMatchType(token, alt);
        if (altMatchType !== 'none') {
          this.totalKeywordMatches++;
          if (altMatchType === 'stem') {
            this.stemMatchCount++;
          }
          tokens.advance();
          return true;
        }
      }
    }

    // Hyphen/underscore-compound fold: many keyword translations are compounds
    // (ms `titik-henti`, es `punto-interrupción`, ru `по_умолчанию`,
    // qu `ñawpaq_kaq`) that the tokenizers shatter into word/sep/word runs —
    // and a segment can be ANOTHER command's keyword (ms `henti` → halt,
    // qu `ñawpaq` → first), so the run mis-anchors that command instead.
    // Join the run and compare it to any separator-bearing expected value;
    // only fires after every single-token match above has failed.
    for (const value of [patternToken.value, ...(patternToken.alternatives ?? [])]) {
      for (const sep of ['-', '_'] as const) {
        if (value.includes(sep) && this.tryMatchShatteredCompound(tokens, value, sep)) {
          this.totalKeywordMatches++;
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Fold a shattered hyphen-compound run into a command action for an {action}
   * role: join `word (- word)+`, look the joined form up among the current
   * profile's command keywords (primary or alternatives, case-insensitive),
   * and return the command's canonical action as a literal. Consumes the run
   * on success, leaves the stream untouched on failure.
   */
  private tryFoldHyphenActionKeyword(tokens: TokenStream): SemanticValue | null {
    const profile = this.currentProfile;
    if (!profile) return null;
    const mark = tokens.mark();
    const parts: string[] = [];
    for (;;) {
      const word = tokens.peek();
      if (!word || (word.kind !== 'identifier' && word.kind !== 'keyword')) break;
      parts.push(word.value);
      tokens.advance();
      const sep = tokens.peek();
      if (!sep || sep.value !== '-') break;
      tokens.advance();
      parts.push('-');
    }
    // A run must end on a word (parts like [w, -, w]); a trailing '-' or a
    // single word means no compound formed.
    if (parts.length < 3 || parts[parts.length - 1] === '-') {
      tokens.reset(mark);
      return null;
    }
    const joined = parts.join('').toLowerCase();
    for (const [action, kw] of Object.entries(profile.keywords)) {
      if (
        kw.primary.toLowerCase() === joined ||
        kw.alternatives?.some(a => a.toLowerCase() === joined)
      ) {
        return createLiteral(action);
      }
    }
    tokens.reset(mark);
    return null;
  }

  /**
   * Match a compound keyword (`titik-henti`, `по_умолчанию`) against a
   * shattered token run (`titik` `-` `henti` / `по` `_` `умолчанию`): word
   * segments must be identifier/keyword/particle tokens equal to the expected
   * segments (case-insensitive; particle because prepositions like ru `по` /
   * uk `за` lead some underscore compounds), separators lone `sep` tokens.
   * Consumes the run on success, resets the stream on failure.
   */
  private tryMatchShatteredCompound(
    tokens: TokenStream,
    expected: string,
    sep: '-' | '_'
  ): boolean {
    const segments = expected.split(sep);
    if (segments.length < 2 || segments.some(s => s.length === 0)) return false;
    const mark = tokens.mark();
    for (let i = 0; i < segments.length; i++) {
      if (i > 0) {
        const sepToken = tokens.peek();
        if (!sepToken || sepToken.value !== sep) {
          tokens.reset(mark);
          return false;
        }
        tokens.advance();
      }
      const word = tokens.peek();
      if (
        !word ||
        (word.kind !== 'identifier' && word.kind !== 'keyword' && word.kind !== 'particle') ||
        word.value.toLowerCase() !== segments[i]!.toLowerCase()
      ) {
        tokens.reset(mark);
        return false;
      }
      tokens.advance();
    }
    return true;
  }

  /**
   * Match a role pattern token, then absorb a trailing `as <Type>` conversion
   * into whatever it captured.
   *
   * `as` is an EXPRESSION operator in hyperscript, not a command role — `attrs.data
   * as JSON` is one value — but every role capture below stops at the value and
   * leaves ` as JSON` unconsumed. The pattern then matched anyway (the trailing
   * tokens are simply dropped) and the conversion vanished: `set ^user to
   * attrs.data as JSON` re-rendered as `set ^user to attrs.data` in English and
   * in all 23 languages. Nothing caught it — the conversion lands in no role, so
   * every recall metric compared two equally truncated things and scored 1.0. The
   * corpus writer's `reRenderPreservesContent` guard is what finally saw it, and
   * it responded by refusing to translate the body at all, which is why
   * `component-with-attrs` was kept from i18n in all 23 languages.
   *
   * Folding the conversion into the captured value's raw is the same lever
   * `tryMatchOperatorRunExpression` uses for `"Hello, " + my value`: one
   * expression value carrying the whole surface, rendered back verbatim.
   *
   * `fetch … as json` is untouched — it has a real `responseType` role whose
   * marker IS `as`, so the pattern's next token wants that token and
   * `patternTokenWouldMatch` declines the fold.
   */
  private matchRoleToken(
    tokens: TokenStream,
    patternToken: PatternToken & { type: 'role' },
    captured: Map<SemanticRole, SemanticValue>,
    nextPatternToken?: PatternToken
  ): boolean {
    const startIdx = tokens.position();
    const before = captured.get(patternToken.role);
    if (!this.matchRoleTokenCore(tokens, patternToken, captured, nextPatternToken)) return false;
    this.absorbTrailingConversion(tokens, patternToken, captured, nextPatternToken, startIdx);
    this.absorbTrailingConditionOperand(tokens, patternToken, captured, nextPatternToken, startIdx);
    this.absorbExpressionTail(tokens, patternToken, captured, nextPatternToken, startIdx);
    this.stampCaptureSpan(tokens, patternToken.role, captured, startIdx, before);
    return true;
  }

  /**
   * Let the expression parser say where a value ends: extend the capture to the
   * longest run the value-extent rule allows that reads as one expression
   * (C13–C16, `utils/value-extent.ts`). Not the event or the action, not a class
   * NAME (`toggle .open in #panel` keeps `.open`, not a scoped query), and not a
   * slot that takes no expression.
   */
  private absorbExpressionTail(
    tokens: TokenStream,
    patternToken: PatternToken & { type: 'role' },
    captured: Map<SemanticRole, SemanticValue>,
    nextPatternToken: PatternToken | undefined,
    startIdx: number
  ): void {
    if (patternToken.role === 'event' || patternToken.role === 'action') return;
    if (this.isClassNameSlot(patternToken)) return;
    if (!captured.has(patternToken.role)) return;
    if (tokens.position() <= startIdx) return;
    const types = patternToken.expectedTypes;
    if (types && types.length > 0 && !types.includes('expression')) return;
    const ends = PatternMatcher.boundedByMarker(nextPatternToken)
      ? valueTailEnds(tokens.tokens, tokens.position(), this.extentContext())
      : possessiveLinkEnds(tokens.tokens, tokens.position(), this.currentProfile);
    const run = longestWholeRun(tokens.tokens, startIdx, ends, this.currentProfile);
    if (!run) return;
    while (tokens.position() < run.end) tokens.advance();
    captured.set(patternToken.role, {
      type: 'expression',
      raw: run.raw,
      value: run.raw,
    } as SemanticValue);
  }

  /** What the value-extent rule needs from the pattern being matched. */
  private extentContext(): ExtentContext {
    const owed = this.restLiterals();
    return {
      profile: this.currentProfile,
      owes: token => owed.some(pt => this.patternTokenWouldMatch(pt, token)),
      commandMarkers: this.commandMarkers(),
      hasResponseType: !!this.currentSchema()?.roles.some(r => r.role === 'responseType'),
    };
  }

  /**
   * Is this slot a class NAME: the patient of add/remove/toggle/take? The
   * role's command, not the pattern's: a fused handler pattern is `on`.
   */
  private isClassNameSlot(patternToken: PatternToken & { type: 'role' }): boolean {
    return (
      patternToken.role === 'patient' &&
      ['add', 'remove', 'toggle', 'take'].includes(this.currentRoleCommand ?? '')
    );
  }

  /** Is this slot a `put` or `set` value, or what a `get` reads, where `{…}` is an object? */
  private isObjectValueSlot(patternToken: PatternToken & { type: 'role' }): boolean {
    const command = this.currentRoleCommand;
    if (patternToken.role === 'source') return command === 'get';
    return patternToken.role === 'patient' && (command === 'put' || command === 'set');
  }

  /** Is the slot followed by a marker (or nothing), which can bound its value? */
  private static boundedByMarker(next: PatternToken | undefined): boolean {
    if (!next) return true;
    if (next.type === 'literal') return true;
    if (next.type === 'group') return next.tokens[0]?.type === 'literal';
    return false;
  }

  /** The literal (marker) tokens of the rest of the pattern, groups included. */
  private restLiterals(): PatternToken[] {
    const out: PatternToken[] = [];
    const walk = (pts: readonly PatternToken[]): void => {
      for (const pt of pts) {
        if (pt.type === 'literal') out.push(pt);
        else if (pt.type === 'group') walk(pt.tokens);
      }
    };
    walk(this.patternRest);
    return out;
  }

  /** The schema of the command the pattern builds, when it has one. */
  private currentSchema(): CommandSchema | undefined {
    const command = this.currentRoleCommand;
    if (!command || !(command in commandSchemas)) return undefined;
    return commandSchemas[command as keyof typeof commandSchemas];
  }

  /**
   * The markers of every role of the command being built, in this language,
   * whichever pattern variant is matching.
   */
  private commandMarkers(): ReadonlySet<string> {
    const schema = this.currentSchema();
    const profile = this.currentProfile;
    const out = new Set<string>();
    if (!schema || !profile) return out;
    for (const role of schema.roles) {
      const override = role.markerOverride?.[profile.code];
      if (override) {
        out.add(override.toLowerCase());
        continue;
      }
      const marker = profile.roleMarkers[role.role];
      if (!marker) continue;
      out.add(marker.primary.toLowerCase());
      for (const alt of marker.alternatives ?? []) out.add(alt.toLowerCase());
    }
    out.delete('');
    return out;
  }

  /**
   * Give a freshly captured role the span of the token run it was built from.
   *
   * This is the ONLY place a role gets a span, deliberately. `matchRoleTokenCore`
   * captures through some twenty branches — a plain single token, but also a
   * dozen folds that assemble one value from a RUN (`myFunction()`,
   * `#dialog.showModal()`, `my value`, `first .item`, `"Hello, " + my value`,
   * `{ left: …px }`), each returning a synthesized value with nowhere to hang a
   * token's position. Stamping the run once, here, covers all of them, cannot
   * be forgotten by a branch added later, and includes whatever
   * `absorbTrailingConversion` / `absorbTrailingConditionOperand` went on to
   * swallow — `attrs.data as JSON` is one value, so it is one span.
   *
   * A stamp inside `tokenToSemanticValue` (in this class and in
   * `semantic-parser.ts`) was written first and then MEASURED dead: with both
   * removed, the parse-path triage, the core span tests and all 9,806 semantic
   * tests were byte-identical, and the ja/ko/tr verb-final paths still reported
   * correct spans. Two stamps for one fact is one too many.
   *
   * Three guards keep it honest. A value that already carries a position keeps
   * it. A slot that captured nothing new — a skipped optional, a marker-only
   * match — leaves the previously captured value alone. And a run of zero
   * tokens yields no span at all, rather than an empty one at the slot's start.
   */
  private stampCaptureSpan(
    tokens: TokenStream,
    role: SemanticRole,
    captured: Map<SemanticRole, SemanticValue>,
    startIdx: number,
    before: SemanticValue | undefined
  ): void {
    const value = captured.get(role);
    if (!value || value === before || value.position) return;
    if (tokens.position() <= startIdx) return;
    const first = tokens.tokens[startIdx];
    const last = tokens.tokens[tokens.position() - 1];
    if (!first || !last) return;
    captured.set(
      role,
      withPosition(value, { start: first.position.start, end: last.position.end })
    );
  }

  /**
   * Condition operator words — the mirror of the parser's own
   * `CONDITION_OPERATORS`, which the condition SCAN uses to know it has not
   * reached the then-branch yet.
   *
   * Matched by NORMALIZED form as well as surface, but in practice it is the
   * surface that hits: `match`/`matches`/`contains`/`includes`/`equals` are
   * emitted verbatim in every language (the renderer has no lexicon entry for
   * them). `has` and `exists` DO localize (de `hat`, ja `ある`, ms `ada`) and
   * their profiles do not normalize the native form back, so
   * `unless me has .off` still truncates its condition in those languages —
   * pre-existing, unchanged by this fold, and the natural next step here.
   */
  private static readonly CONDITION_OPERATOR_WORDS: ReadonlySet<string> = new Set([
    'matches',
    'match',
    'contains',
    'exists',
    'has',
    'have',
    'equals',
    'includes',
  ]);

  /**
   * Comparison and logical operators a LOOP condition continues through, by
   * surface (the en tokenizer classifies `<`/`<=` as selectors, since `<` can open
   * a query literal, and `!=` as an operator) plus `and`/`or` by normalized form.
   */
  private static readonly LOOP_CONDITION_OPERATORS: ReadonlySet<string> = new Set([
    ...COMPARISON_OPERATORS,
    'and',
    'or',
  ]);

  /**
   * Extend a `{condition}` capture with a trailing `<operator> [operand]`.
   *
   * A fused event pattern ends in a bare `{condition}` slot — tl
   * `kapag {event} maliban_kung {condition}` — and the slot captures ONE token.
   * `unless I match .disabled toggle .selected` therefore bound `condition: I`
   * and left `match .disabled` unconsumed, so the guard tested the element
   * itself and the class it was supposed to check vanished. Nine languages
   * (ms/pl/pt/ru/sw/th/tl/uk/vi) render this shape; English escapes only because
   * its handler pattern hands the whole body to the clause walk, whose condition
   * scan already knows these words.
   *
   * One operator and at most one operand, and the operand must not be a command
   * verb: `unless #x exists toggle .y` has to leave `toggle` for the body. A
   * keyword-kind token is never taken, which covers the localized verbs, and
   * `COMMAND_ACTION_KEYWORDS` covers the ones a tokenizer classifies as
   * identifiers.
   */
  private absorbTrailingConditionOperand(
    tokens: TokenStream,
    patternToken: PatternToken & { type: 'role' },
    captured: Map<SemanticRole, SemanticValue>,
    nextPatternToken: PatternToken | undefined,
    startIdx: number
  ): void {
    if (patternToken.role !== 'condition') return;
    if (!captured.has('condition')) return;
    if (tokens.position() <= startIdx) return;
    // The TRAILING condition slot of a FUSED EVENT pattern, or a LOOP head's
    // condition. Elsewhere the condition is followed by its own branches, and
    // the clause walk's condition scan — which already knows these operator
    // words — is what delimits it. Measured: without this gate the fold fires on
    // the bare `if #modal exists show #modal else … end`, whose en parse then
    // collapses to `if #modal exists` with both branches gone (bn/tl/tr on the
    // bare-render gate). A loop head has no branches: its body follows as
    // sibling commands, which the operand guard below leaves alone.
    //
    // A loop condition also continues through comparisons: the `repeat while`
    // head captured only `#counter.innerText` of `#counter.innerText < 10`, so
    // the English reference — and every translation of it — lost `< 10`, and
    // the loop no longer ended.
    const inLoop = this.currentPatternCommand === 'repeat';
    if (!inLoop && (nextPatternToken !== undefined || this.currentPatternCommand !== 'on')) return;

    const words = PatternMatcher.CONDITION_OPERATOR_WORDS;
    const isOperator = (t: LanguageToken): boolean => {
      const normalized = (t.normalized ?? '').toLowerCase();
      const value = t.value.toLowerCase();
      if (words.has(normalized) || words.has(value)) return true;
      const loopOps = PatternMatcher.LOOP_CONDITION_OPERATORS;
      return inLoop && (loopOps.has(value) || loopOps.has(normalized));
    };
    const isOperand = (t: LanguageToken | null): t is LanguageToken =>
      !!t &&
      (t.kind === 'selector' || t.kind === 'identifier' || t.kind === 'literal') &&
      !isOperator(t) &&
      !COMMAND_ACTION_KEYWORDS.has((t.normalized ?? t.value).toLowerCase()) &&
      !this.patternTokenWouldMatch(nextPatternToken, t);

    let folded = false;
    for (;;) {
      const op = tokens.peek();
      if (!op || !isOperator(op) || this.patternTokenWouldMatch(nextPatternToken, op)) break;
      tokens.advance(); // the operator
      folded = true;
      let operand = tokens.peek();
      if (isOperand(operand)) {
        tokens.advance();
        // A member glued to the operand (`#max.value`) belongs to it.
        for (let next = tokens.peek(); ; next = tokens.peek()) {
          if (
            !next ||
            !/^\.[a-zA-Z_]\w*$/.test(next.value) ||
            next.position.start !== operand.position.end
          ) {
            break;
          }
          tokens.advance();
          operand = next;
        }
      }
      // A fused event slot takes one operator; a loop condition may chain
      // (`x < 10 and y > 2`).
      if (!inLoop) break;
    }
    if (!folded) return;

    const raw = joinExpressionTokens(
      tokens.tokens.slice(startIdx, tokens.position()),
      this.currentProfile
    );
    captured.set(
      'condition' as SemanticRole,
      {
        type: 'expression',
        raw,
        value: raw,
      } as SemanticValue
    );
  }

  /**
   * How many tokens of conversion is the stream sitting on: `as`, a type, and
   * each further `| type` (`as JSONString | JSON`, which converts left to
   * right)? 0 when it is not on one. Matched by VALUE as well as normalized
   * form: the renderer emits the conversion verbatim inside the value's raw
   * (no profile has an `as` lexicon entry), so a foreign surface carries the
   * English word — which is what has to re-parse.
   */
  private conversionRunLength(tokens: TokenStream): number {
    const asToken = tokens.peek();
    if (!asToken) return 0;
    const asWord = (asToken.normalized ?? asToken.value).toLowerCase();
    if (asWord !== 'as' && asToken.value.toLowerCase() !== 'as') return 0;
    let length = this.conversionTypeLength(tokens, 1);
    if (length === 0) return 0;
    length += 1;
    for (;;) {
      if (tokens.peek(length)?.value !== '|') return length;
      const next = this.conversionTypeLength(tokens, length + 1);
      if (next === 0) return length;
      length += 1 + next;
    }
  }

  /**
   * How many tokens of conversion type start `offset` tokens ahead: an optional
   * article (`as an Int`), then a type name downstream reads. A non-English
   * tokenizer splits `Fixed:2` (and `Values:Form`) at its colon, into three
   * adjacent tokens.
   */
  private conversionTypeLength(tokens: TokenStream, offset: number): number {
    const article = tokens.peek(offset)?.value.toLowerCase();
    const at = article === 'a' || article === 'an' ? offset + 1 : offset;
    const type = tokens.peek(at);
    if (!type) return 0;
    const colon = tokens.peek(at + 1);
    const suffix = tokens.peek(at + 2);
    if (
      colon?.value === ':' &&
      suffix &&
      tokensAdjacent(type, colon) &&
      tokensAdjacent(colon, suffix) &&
      isConversionTypeName(`${type.value}:${suffix.value}`)
    ) {
      return at - offset + 3;
    }
    return isConversionTypeName(type.value) ? at - offset + 1 : 0;
  }

  /** Extend `patternToken.role`'s captured value with a trailing `as <Type>`. */
  private absorbTrailingConversion(
    tokens: TokenStream,
    patternToken: PatternToken & { type: 'role' },
    captured: Map<SemanticRole, SemanticValue>,
    nextPatternToken: PatternToken | undefined,
    startIdx: number
  ): void {
    // An event name is never an operand of a conversion, and an {action} slot
    // captures a verb.
    if (patternToken.role === 'event' || patternToken.role === 'action') return;
    if (!captured.has(patternToken.role)) return;
    if (tokens.position() <= startIdx) return;
    const length = this.conversionRunLength(tokens);
    if (length === 0) return;
    // The pattern itself wants this token (fetch's `as {responseType}`) — leave it.
    if (this.patternTokenWouldMatch(nextPatternToken, tokens.peek()!)) return;

    for (let i = 0; i < length; i++) tokens.advance(); // `as` and the types
    const raw = joinExpressionTokens(
      tokens.tokens.slice(startIdx, tokens.position()),
      this.currentProfile
    );
    captured.set(patternToken.role, { type: 'expression', raw, value: raw } as SemanticValue);
  }

  /**
   * Match a role pattern token (captures a semantic value).
   * Handles multi-token expressions like:
   * - 'my value' (possessive keyword + property)
   * - '#dialog.showModal()' (method call)
   * - "#element's *opacity" (possessive selector + property)
   */
  private matchRoleTokenCore(
    tokens: TokenStream,
    patternToken: PatternToken & { type: 'role' },
    captured: Map<SemanticRole, SemanticValue>,
    nextPatternToken?: PatternToken
  ): boolean {
    // Skip noise words like "the" before selectors (English idiom support)
    this.skipNoiseWords(tokens, patternToken, nextPatternToken);

    const token = tokens.peek();
    if (!token) {
      return patternToken.optional || false;
    }
    const slot = this.slotAt(tokens, patternToken, nextPatternToken);

    // Action-role hyphen-compound fold (same family as the matchLiteralToken
    // fold): a fused event pattern's {action} slot captures ONE token, but a
    // hyphen-compound verb translation (it `punto-interruzione`) shatters into
    // a word/-/word run — the slot then captures the first fragment as a junk
    // action and the whole command drops. Join the run; if it is a profile
    // keyword for a command, capture that command's canonical action instead.
    if (patternToken.role === 'action' && this.currentProfile && tokens.peek(1)?.value === '-') {
      const foldedAction = this.tryFoldHyphenActionKeyword(tokens);
      if (foldedAction) {
        captured.set(patternToken.role as SemanticRole, foldedAction);
        return true;
      }
    }

    // Event-anchor guard. An event name is a bare identifier or known event
    // keyword (`click`, `keyup`, a custom `myEvent`) — NEVER a selector, URL,
    // method call (`fn()`), or string/number literal. In an SOV reorder the
    // object is fronted ahead of the verb, so a bare-event pattern (`{event}`)
    // or `{event} <marker>` pattern would otherwise mis-anchor a fronted
    // `/api/data` / `myFunction()` as the handler event (every role matched any
    // token). Reject non-event-shaped tokens for the `event` role so the input
    // falls through to the command stage (or the per-command SOV event pattern
    // whose patient role legitimately captures it). See
    // docs-internal/HANDOFF-sov-event-anchor.md.
    if (patternToken.role === 'event' && this.currentPatternCommand === 'on') {
      if (!PatternMatcher.tokenLooksLikeEvent(token)) {
        return patternToken.optional || false;
      }
      // R1 cluster C (the surviving tail of the #508 guard): a fronted
      // POSITIONAL phrase or POSSESSIVE head is never an event either — the
      // expression assemblers below would otherwise capture it wholesale into
      // the event role (hi `पहला <input/> …` → event:expression via the
      // positional matcher, focus-patient defaults to me; hi `मेरा @data-count
      // को लोड पर डिफ़ॉल्ट …` → event:property-path via the possessive matcher,
      // the default body gets garbage roles). Rejecting here lets the input
      // fall through to the per-command pattern whose patient/destination
      // legitimately captures the fronted phrase — the same fall-through
      // contract as tokenLooksLikeEvent.
      const evNorm = (token.normalized ?? token.value).toLowerCase();
      if (PatternMatcher.POSITIONAL_OR_SCOPE_KEYWORDS.has(evNorm)) {
        return patternToken.optional || false;
      }
      // A hide/show strategy is never an event either. ja marks both with
      // `で`, so a bare `自分 を opacity で 隠す` (hide me with opacity) is also
      // the shape of its patient-first handler (`#x を click で 隠す`, on click
      // hide #x), and read `on opacity hide me`.
      if (PatternMatcher.VISIBILITY_STRATEGIES.has(evNorm)) {
        return patternToken.optional || false;
      }
      if (
        this.currentProfile &&
        (getPossessiveReference(this.currentProfile, token.value) ??
          getPossessiveReference(this.currentProfile, evNorm)) !== undefined
      ) {
        return patternToken.optional || false;
      }
      // An optional-chaining head fused into one token (hi
      // `मेरा?.dataset?.customValue`) is a property access, never an event.
      if (token.value.includes('?.')) {
        return patternToken.optional || false;
      }
    }

    // A STRUCTURAL keyword (if/unless/else/end/then) is never an event payload
    // on ANY command — unlike the shape guard above, this applies beyond `on`.
    // The SOV verb-first trigger pattern (`引き金 {event}`) otherwise swallows
    // the `もし` (if) that opens the NEXT juxtaposed clause as its event
    // (`removable:before を 引き金` + `もし effect …` → event:literal="if"),
    // which both fabricates a junk event AND hides the if-keyword from the
    // conditional folds — the whole `if effect is "fade" transition …` block
    // then degrades to junk verb-anchoring (behavior-removable's transition
    // roles, SOV six). The send/trigger payload contract (string literal or
    // identifier event names, see the currentPatternCommand note) is untouched:
    // no real event is ever named `if`/`end`/`else`/`unless`/`then`. `init` is
    // deliberately NOT listed — `trigger init` is a legitimate corpus event.
    if (patternToken.role === 'event') {
      const evStructNorm = (token.normalized ?? token.value).toLowerCase();
      if (PatternMatcher.STRUCTURAL_NEVER_EVENT.has(evStructNorm)) {
        return patternToken.optional || false;
      }
    }

    // Structure that is never a value (C3), and a command verb that begins the
    // next command, not a count (C4). See value-reading.ts.
    if (
      neverAValue(slot, this.currentProfile?.code ?? '') ||
      verbEndsCountSlot(slot, this.currentPatternCommand)
    ) {
      return patternToken.optional || false;
    }

    // A `duration` slot is never a positional/scope keyword. The temporal
    // `in {duration}` idiom (`in 2s`) otherwise greedily swallows a *locative*
    // `in closest <form/>` (the scope of `focus first <input/> in closest <form/>`)
    // and spuriously emits a `wait` — which corrupted the English *reference* parse
    // (`focus first … in closest …` → {focus, wait}) and made first-in-parent /
    // focus-trap read as lossy in every other language. `closest` tokenizes as a
    // `literal` (same type as `2s`), so this keyword guard is the disambiguator.
    if (patternToken.role === 'duration') {
      const norm = (token.normalized ?? token.value).toLowerCase();
      if (PatternMatcher.POSITIONAL_OR_SCOPE_KEYWORDS.has(norm)) {
        return patternToken.optional || false;
      }
    }

    // A command verb the slot must not take: skip the optional slot (C5).
    if (verbSkipsOptionalSlot(slot)) return true;

    // Owner-first possessive with a POSITIONAL owner (ja `前 <output/> の textContent`,
    // ko `이전 <output/> 의 …`, zh/bn/hi/tl/vi alike) — the render of `the
    // textContent of the previous <output/>` in the clitic languages. Must run
    // before the positional matcher below, which would take the run alone and
    // strand `の textContent`. Gated like the of-possessive matcher.
    if (this.roleAcceptsPropertyPath(patternToken)) {
      const positionalPossessive = this.tryMatchPositionalPossessiveExpression(
        tokens,
        nextPatternToken
      );
      if (positionalPossessive) {
        captured.set(patternToken.role, positionalPossessive);
        return true;
      }
    }

    // Check for a positional query expression (e.g., 'last <.message/> in #chat',
    // 'first <button/> in .modal'). Triggered only when the role starts with a
    // positional keyword, so non-positional roles are unaffected.
    const positionalValue = this.tryMatchPositionalExpression(tokens, nextPatternToken);
    if (positionalValue) {
      if (patternToken.expectedTypes && patternToken.expectedTypes.length > 0) {
        if (!isTypeCompatible(positionalValue.type, patternToken.expectedTypes)) {
          return patternToken.optional || false;
        }
      }
      captured.set(patternToken.role, positionalValue);
      return true;
    }

    // Check for a caret-scoped variable read (e.g. `^count on #host` — a
    // DOM-scoped `^name` variable read from a specific element). The `^` prefix
    // disambiguates this `on` from the event/destination `on`, so a normal
    // `toggle .active on #button` (selector patient) is never affected. Folds
    // `^name on <element>` into one expression value so a following destination
    // (`into me`) still matches.
    const caretScopeValue = this.tryMatchCaretScopeExpression(tokens);
    if (caretScopeValue) {
      if (patternToken.expectedTypes && patternToken.expectedTypes.length > 0) {
        if (!isTypeCompatible(caretScopeValue.type, patternToken.expectedTypes)) {
          return patternToken.optional || false;
        }
      }
      captured.set(patternToken.role, caretScopeValue);
      return true;
    }

    // A method call on a parenthesized receiver: `(closest <form/>).submit()` —
    // the `call` spelling of a pseudo-command whose target is a query (see
    // ../parser/pseudo-command.ts). Requires the `.method(…)` tail, so an
    // ordinary parenthesized operand (`(the value of #price as Number) * …`)
    // is left to the operator-run matcher below.
    const parenCallValue = this.tryMatchParenReceiverCall(tokens);
    if (parenCallValue) {
      if (
        patternToken.expectedTypes &&
        patternToken.expectedTypes.length > 0 &&
        !patternToken.expectedTypes.includes('expression')
      ) {
        return patternToken.optional || false;
      }
      captured.set(patternToken.role, parenCallValue);
      return true;
    }

    // Multi-token OPERATOR-RUN expression (cluster A2): `"Hello, " + my value`,
    // `(the value of #price as Number) * (my value as Number)`. The tokenizers
    // split these into value/operator/value token runs, so a single-token role
    // capture takes only the first operand and the pattern then dies on the
    // operator — the en reference silently dropped the `+ my value` tail
    // (patient:literal="Hello, ") and every SOV set fell through to the
    // role-swapping verb-anchoring fallback. Assembling the whole run into one
    // expression value lets the surrounding pattern match at position 0. Strictly
    // pairwise (value OP value (OP value)*): it fires only when a binary operator
    // DIRECTLY follows the first operand, so every non-arithmetic capture is
    // untouched. Skipped for the event role (an event name is never an operand).
    if (
      patternToken.role !== 'event' &&
      (!patternToken.expectedTypes || patternToken.expectedTypes.includes('expression'))
    ) {
      // `beep! <expr>` first: its head (`beep`) is a plain value token, so the
      // single-token capture below would otherwise take it and strand the
      // `! <expr>` tail as unconsumed — the same silent truncation the operator
      // run fixes for `"Hello, " + my value`, in prefix form.
      const beepValue = this.tryMatchBeepPrefixExpression(tokens);
      if (beepValue) {
        captured.set(patternToken.role, beepValue);
        return true;
      }
      // `non-modal` before the operator run, which would read the hyphen as
      // subtraction and hand the runtime a binaryExpression no string compare
      // can match.
      const hyphenValue = this.tryMatchHyphenatedWord(tokens);
      if (hyphenValue) {
        captured.set(patternToken.role, hyphenValue);
        return true;
      }
      const runValue = this.isWrittenTarget(patternToken)
        ? null
        : this.tryMatchOperatorRunExpression(tokens);
      if (runValue) {
        captured.set(patternToken.role, runValue);
        return true;
      }
    }

    // Multi-token PICK RANGE expression: `0 to 5`, `start to end`,
    // `0 to 5 inclusive`. The canonical `pick (item|character)s <range> of <root>`
    // range sub-grammar is `[at|from] (start|<expr>) [(to|..) (end|<expr>)]
    // [inclusive|exclusive]`, but a single-token {patient} slot captures only the
    // first endpoint (`0`) and the pattern then dies on the ` to 5 …` tail — which
    // is exactly why the en reference of `pick characters 0 to 5 of #note`
    // truncated to `pick characters` (dropped `0 to 5 of #note`) and failed the
    // canonical parser (the lone pick-text-range canonical-validity allowlist
    // entry). Folding the whole range into one expression value (raw `0 to 5`)
    // lets the surrounding pick pattern's trailing `of {source}` still match, and
    // the value renders verbatim (renderer `valueToNaturalString` case
    // 'expression' → value.raw). The pick mapper splits the raw back into
    // rangeStart/rangeEnd/rangeMode for the core AST.
    //
    // Gated to the pick command's {patient} role: the range separator is a bare
    // `to` between two operands, which no other command's patient carries,
    // and — critically — no FOREIGN pick translation uses a literal English `to`
    // (es `a`, ru `в`, ja `を`), so the generated foreign `pick {patient} …`
    // patterns decline this fold and parse byte-identically. Arcs 2/3 widen the
    // separator to per-profile range markers.
    if (
      this.currentPatternCommand === 'pick' &&
      patternToken.role === 'patient' &&
      (!patternToken.expectedTypes || patternToken.expectedTypes.includes('expression'))
    ) {
      const rangeValue = this.tryMatchPickRangeExpression(tokens);
      if (rangeValue) {
        captured.set(patternToken.role, rangeValue);
        return true;
      }
    }

    // Check for an "of"-possessive expression (e.g. "*--primary-color of #theme",
    // AR "*--primary-color من #theme", TL "*--primary-color ng #theme"). Gated to
    // roles that opt into property-path (set/default's destination, bind's
    // source, increment/decrement's patient), so the "of"/source marker can't be
    // confused with a real source role on other commands (e.g. `get data from
    // #input`).
    if (this.roleAcceptsPropertyPath(patternToken)) {
      const ofPossessive = this.tryMatchOfPossessiveExpression(tokens, nextPatternToken);
      if (ofPossessive) {
        captured.set(patternToken.role, ofPossessive);
        return true;
      }
      // Post-nominal (property-first) possessive: `textContent لي` (ar),
      // `textContent saya` (id), `textContent yangu` (sw) — the possessor follows
      // the property in `after-object` languages. The pre-nominal matcher below
      // only handles possessor-first (`my value`), so these either failed to parse
      // (ar/id/sw) or parsed lossily, dropping the possessor (ru/pl/uk). Gated to
      // property-path roles (set's destination) so it can't misread an ordinary
      // role value. See MULTILINGUAL_BEHAVIORS_PLAN.md Phase 2 tail.
      const postNominalPossessive = this.tryMatchPostNominalPossessiveExpression(tokens);
      if (postNominalPossessive) {
        captured.set(patternToken.role, postNominalPossessive);
        return true;
      }
    }

    // Check for possessive expression (e.g., 'my value', 'its innerHTML')
    const possessiveValue = this.tryMatchPossessiveExpression(tokens);
    if (possessiveValue) {
      // Validate expected types if specified
      if (patternToken.expectedTypes && patternToken.expectedTypes.length > 0) {
        if (
          !patternToken.expectedTypes.includes(possessiveValue.type) &&
          !patternToken.expectedTypes.includes('expression')
        ) {
          return patternToken.optional || false;
        }
      }
      captured.set(patternToken.role, possessiveValue);
      return true;
    }

    // Check for method call expression (e.g., '#dialog.showModal()')
    const methodCallValue = this.tryMatchMethodCallExpression(tokens);
    if (methodCallValue) {
      if (patternToken.expectedTypes && patternToken.expectedTypes.length > 0) {
        if (
          !patternToken.expectedTypes.includes(methodCallValue.type) &&
          !patternToken.expectedTypes.includes('expression')
        ) {
          return patternToken.optional || false;
        }
      }
      captured.set(patternToken.role, methodCallValue);
      return true;
    }

    // Check for a bare function-call expression (e.g., `myFunction()`,
    // `adjustLayout(a, b)`). The multilingual tokenizers split this into
    // `myFunction` `(` `…` `)` (the parens tokenize as identifiers, not
    // punctuation, so `tryMatchMethodCallExpression` — which needs a
    // `selector.method()` shape — does not fold it). Verb-first SVO/VSO leaves
    // the dangling `( )` harmlessly after a captured `{patient}`, but a
    // verb-final SOV role (`{patient} <marker> … <verb>`) captures only the
    // bare name, and the dangling `( )` then break the marker match — dropping
    // the whole command (qu/bn `call myFunction()` → NULL) or letting a
    // bare-event pattern mis-anchor the name (hi). Fold the whole `name(...)`
    // into one expression value so the role captures it cleanly.
    //
    // Skipped in two cases:
    //  - the `on` HANDLER's `event` role: `on pointerdown(clientX, clientY)`
    //    destructures EVENT PARAMS, not a call — the event name (`pointerdown`)
    //    must be captured alone, with params handled by the event-handler
    //    builder (folding would corrupt the event name to
    //    `pointerdown(clientX, clientY)`). A COMMAND's event role (`send
    //    update(value: 42) to #target`, trigger likewise) is the opposite: the
    //    call shape IS the event-with-detail, and skipping the fold leaves a
    //    dangling `( )` that breaks the following destination group — en
    //    truncated the event to `literal="update"` AND dropped `to #target`
    //    (send-with-detail ×21; translations captured both and were penalized).
    //  - DECLARATION commands (`behavior`/`def`/`install`): `Draggable(dragHandle)`
    //    is a declaration SIGNATURE, not a call. Folding it lets the single-command
    //    declaration pattern (e.g. SOV `{name} কে আচরণ`) consume the first line of a
    //    multi-line `behavior … end` block and return a degenerate match, shadowing
    //    the faithful full-block parse (bn draggable/sortable/…). Leaving the
    //    dangling `(params)` makes that pattern fail, so the block falls through to
    //    the faithful path; the header's params are read separately by parseHeader.
    const skipBareCall =
      (patternToken.role === 'event' && this.currentPatternCommand === 'on') ||
      PatternMatcher.DECLARATION_COMMANDS.has(this.currentPatternCommand ?? '') ||
      this.isWrittenTarget(patternToken);
    const bareCallValue = skipBareCall ? null : this.tryMatchBareCallExpression(tokens);
    if (bareCallValue) {
      if (patternToken.expectedTypes && patternToken.expectedTypes.length > 0) {
        if (
          !patternToken.expectedTypes.includes(bareCallValue.type) &&
          !patternToken.expectedTypes.includes('expression')
        ) {
          return patternToken.optional || false;
        }
      }
      captured.set(patternToken.role, bareCallValue);
      return true;
    }

    // Check for possessive selector expression (e.g., "#element's *opacity")
    const possessiveSelectorValue = this.tryMatchPossessiveSelectorExpression(tokens);
    if (possessiveSelectorValue) {
      if (patternToken.expectedTypes && patternToken.expectedTypes.length > 0) {
        // property-path is compatible with selector, reference, and expression
        if (!isTypeCompatible(possessiveSelectorValue.type, patternToken.expectedTypes)) {
          return patternToken.optional || false;
        }
      }
      captured.set(patternToken.role, possessiveSelectorValue);
      return true;
    }

    // Check for property access expression (e.g., 'userData.name', 'it.data')
    // The condition position keeps the raw-span expression form: the en
    // reference's `if event.ctrlKey` is captured as a raw span (never routed
    // through this matcher), so flipping `<ref>.<prop>` to property-path here
    // would mismatch en for the SOV langs that DO route the condition through
    // the matcher (ja/ko/qu window-keydown). Scope the property-path emission
    // out of the condition role. (Role-NAME scoping, not expectedTypes —
    // `put.patient` and `if.condition` both omit property-path, relying on the
    // isTypeCompatible rule, so a type-list scope can't separate them.)
    const allowPropertyPath = patternToken.role !== 'condition';
    const propertyAccessValue = this.tryMatchPropertyAccessExpression(tokens, allowPropertyPath);
    if (propertyAccessValue) {
      if (patternToken.expectedTypes && patternToken.expectedTypes.length > 0) {
        if (
          !patternToken.expectedTypes.includes(propertyAccessValue.type) &&
          !patternToken.expectedTypes.includes('expression')
        ) {
          return patternToken.optional || false;
        }
      }
      captured.set(patternToken.role, propertyAccessValue);
      return true;
    }

    // Check for selector + property expression (e.g., '#output.innerText')
    // This handles cases where the tokenizer produces two selector tokens
    const selectorPropertyValue = this.tryMatchSelectorPropertyExpression(tokens);
    if (selectorPropertyValue) {
      if (patternToken.expectedTypes && patternToken.expectedTypes.length > 0) {
        if (!isTypeCompatible(selectorPropertyValue.type, patternToken.expectedTypes)) {
          return patternToken.optional || false;
        }
      }
      captured.set(patternToken.role, selectorPropertyValue);
      return true;
    }

    // A parenthesized group standing alone as the value (`hide (closest .modal)`).
    // Placed after every expression matcher above, so the operator run, the
    // paren receiver call and the possessive forms keep their shapes; this only
    // replaces the single-token capture below, which took the lone `(`. Admitted
    // wherever a TARGET is — a group is how a computed target is written — so a
    // `selector|reference` slot takes it, not only an `expression` one.
    if (
      token.value === '(' &&
      patternToken.role !== 'event' &&
      patternToken.role !== 'action' &&
      (!patternToken.expectedTypes?.length ||
        patternToken.expectedTypes.some(
          t => t === 'expression' || t === 'selector' || t === 'reference'
        ))
    ) {
      const group = this.tryMatchParenGroupExpression(tokens);
      if (group) {
        captured.set(patternToken.role, group);
        return true;
      }
    }

    // Brace-run STYLE-OBJECT literal fold: `{ left: ${clientX - xoff}px; … }`
    // shatters into ~12+ identifier tokens (behavior-draggable's add patient —
    // en and the generated-path languages captured only the lone `{` or died
    // on it, while the SOV lax path fuses the run to ONE literal). A
    // standalone `{` identifier token is unambiguous: `.{cls}` dynamic class
    // selectors tokenize as ONE selector token, so no collision. Depth-tracked
    // for the nested `${…}` template braces; fires for roles that accept a
    // literal or declare no expectedTypes (the handcrafted add-*-full patient
    // — capturing a lone `{` is never right), and only when the run closes —
    // an unbalanced `{` backtracks.
    // An expression-ONLY slot (fetch's `with { … }` request options) takes the
    // object-literal fold instead, preserving source text so the expression parser
    // can build a real objectLiteral node. Slots that also accept a literal keep
    // the literal fold — switching them would retype every generated-path
    // `['literal','expression']` style-object capture.
    //
    // A `put` or `set` value is an object too (`set x to {a: 1}`), though its
    // slot accepts a literal: the literal fold made it the TEXT `{ a : 1 }`,
    // which the direct path wrote as a string. The command's role decides, not
    // the content: a one-property style block and a one-key object look alike.
    if (token.kind === 'identifier' && token.value === '{') {
      const types = patternToken.expectedTypes;
      const expressionOnly =
        (types?.length === 1 && types[0] === 'expression') || this.isObjectValueSlot(patternToken);
      const braceRun = expressionOnly
        ? this.tryMatchBraceRunExpression(tokens)
        : !types?.length || types.includes('literal')
          ? this.tryMatchBraceRunLiteral(tokens)
          : null;
      if (braceRun) {
        captured.set(patternToken.role, braceRun);
        return true;
      }
    }

    // Naked named-arg fold — the brace-less sibling of the object-literal fold
    // above, for fetch's request-options slot ONLY (`fetch /x with
    // method:"POST" body:form`). Without it the expression-only slot captures
    // the first key (`method`) and the rest of the run drops as unconsumed
    // input (the Arc C fetch-options family). Role-NAME scoped like the
    // condition scoping above: `style` is the request-options role, and the
    // pair grammar (`key : value`, key code-shaped) never matches a legitimate
    // single-token style value.
    if (
      patternToken.role === 'style' &&
      patternToken.expectedTypes?.length === 1 &&
      patternToken.expectedTypes[0] === 'expression'
    ) {
      const nakedRun = foldNakedNamedArgsRaw(tokens);
      if (nakedRun) {
        captured.set(patternToken.role, { type: 'expression', raw: nakedRun } as SemanticValue);
        return true;
      }
    }

    // A word spelled like structure, alone where a value stands, is the variable
    // it spells: a particle where no marker can stand (C7), a structure keyword
    // or command verb (C8). See value-reading.ts.
    if (
      particleIsValue(slot) ||
      keywordIsVariable(slot, this.currentSchema()) ||
      endWordIsVariable(slot, this.currentProfile?.code ?? '')
    ) {
      captured.set(patternToken.role, { type: 'expression', raw: token.value } as SemanticValue);
      tokens.advance();
      return true;
    }

    // Try to extract a semantic value from the token
    const value = this.tokenToSemanticValue(token);
    if (!value) {
      return patternToken.optional || false;
    }

    // Validate expected types if specified
    if (patternToken.expectedTypes && patternToken.expectedTypes.length > 0) {
      if (!patternToken.expectedTypes.includes(value.type)) {
        // Canonical `@attr` typing: an attribute reference is ALWAYS a
        // selector (tokenToSemanticValue), regardless of which slot captures
        // it — the old slot-dependent reading (selector when the slot expected
        // one, expression when lax) made the SAME token type-diverge between
        // en's patterns and the generated event-role slots (add `@disabled`
        // en=selector / 18 langs=expression; toggle `@aria-expanded` the exact
        // opposite). Slots that relied on the identifier/expression reading
        // (bind's `@property`, expectedTypes ['reference','expression']) still
        // capture it — as the canonical selector.
        const isAttrRef =
          value.type === 'selector' && String((value as { value: unknown }).value).startsWith('@');
        const slotTakesExpr = patternToken.expectedTypes.some(
          t => t === 'expression' || t === 'reference'
        );
        if (!(isAttrRef && slotTakesExpr)) {
          return patternToken.optional || false;
        }
      }
    }

    // A `keyword`-shaped slot holds a FIXED keyword phrase, so its value type is
    // a property of the slot, not of the language's vocabulary. `using view
    // {manner}` captures the word `transition`, which en/fr register as a command
    // keyword (it is also their verb) and the other 22 tokenizers leave as a bare
    // identifier — so the SAME word lands as `literal` in en and `expression`
    // everywhere else. R1 compares `action.role:valueType`, so that asymmetry
    // alone reads as a dropped role in 22 languages (the pick-unit-word class,
    // #868, in mirror image). Normalize to the EN reference's shape. Placed after
    // `expectedTypes` validation, so no type-compatibility or adoption decision
    // can move — the slot still admits both types, it just reports one.
    if (patternToken.valueShape === 'keyword' && value.type === 'expression') {
      captured.set(
        patternToken.role,
        createLiteral(String((value as { raw?: unknown }).raw ?? ''))
      );
    } else {
      captured.set(patternToken.role, value);
    }
    tokens.advance();

    // A query's locative scope: `add @disabled to <button/> in me`, `remove
    // <li/> in #list`. A `<…/>` query takes one, and so does a class ref
    // (`set x to .item in #list`, `add .z to .item in #list`, as upstream
    // reads it), except where the class is a class NAME, the patient of
    // add/remove/toggle/take: it's `a` ("to") is also a locative, so
    // `aggiungere .a a <li/> in #list` read the name as a query scoped to the
    // `<li/>`. The role's command, not the pattern's: a fused handler pattern
    // is `on`. Never as an event. Without this the `in …` tail went
    // unconsumed, and the query lost its scope in English and so in every
    // translation.
    const classNameSlot = this.isClassNameSlot(patternToken);
    const takesScope =
      value.type === 'selector' &&
      (value.value.startsWith('<') || (value.value.startsWith('.') && !classNameSlot));
    if (takesScope && patternToken.role !== 'event') {
      const scoped = matchQueryScope(tokens.tokens, tokens.position(), this.currentProfile, t =>
        t === undefined ? false : this.patternTokenWouldMatch(nextPatternToken, t)
      );
      if (scoped) {
        const [first, second] = scoped.scope.parts;
        const scopeValue: SemanticValue =
          scoped.scope.kind === 'reference'
            ? createReference(first.text as ReferenceValue['value'])
            : scoped.scope.kind === 'closest'
              ? ({ type: 'expression', raw: `closest ${second.text}` } as SemanticValue)
              : (this.tokenToSemanticValue(first.token) as SemanticValue);
        for (let n = 0; n < 1 + scoped.scope.consumed; n++) tokens.advance();
        captured.set(patternToken.role, { ...value, scope: scopeValue });
      }
    }

    // Event-head tolerance: a bracket key-filter and/or a prepositional source
    // clause can trail the event token (`keydown` + `[key=="Tab"]` — the
    // tokenizer splits them — then `von .modal`). Fused event patterns
    // (`<marker> {event} <verb> …`) expect the wrapped command's verb right
    // after the event role, so without consuming these the whole fused match
    // fails and the input falls to a plain event pattern whose body re-parse
    // drops the wrapped block command (focus-trap's `if` across de/it/es/fr/pt).
    // Mirrors the bracket-filter skip already in the SOV/mid-stream extractors.
    if (patternToken.role === 'event') {
      const filterTok = tokens.peek();
      // A filter touches its event (`keydown[key=="Escape"]`, split by the
      // tokenizer). A spaced `[…]` is the next value: qu writes `put [1, 2]
      // into x` as `click [1, 2] ta x man churay`, the array after the event.
      if (
        filterTok &&
        filterTok.kind === 'selector' &&
        filterTok.value.startsWith('[') &&
        filterTok.position.start === token.position.end &&
        'value' in value
      ) {
        // Fold the filter back onto the event value (the tokenizer split it off).
        captured.set(patternToken.role, createLiteral(`${String(value.value)}${filterTok.value}`));
        tokens.advance();
      }
      // `<source-marker> <element>` (`von .modal` / `kutoka .modal` / `from window`).
      // Skipped when the pattern itself expects the marker next (e.g. the
      // handcrafted `event-de-bei-source` carries an explicit `von {source}`).
      // Captured into `eventSource`, never into `captured.source`: the pattern's
      // own trailing `[de {source}]` group is the BODY command's from-phrase
      // and must neither overwrite this nor be overwritten by it.
      const srcMarker = tokens.peek();
      if (
        srcMarker &&
        (srcMarker.kind === 'particle' || srcMarker.kind === 'keyword') &&
        (srcMarker.normalized ?? '').toLowerCase() === 'source' &&
        !this.patternTokenWouldMatch(nextPatternToken, srcMarker)
      ) {
        const srcValueTok = tokens.peek(1);
        const srcValue = srcValueTok ? this.sourceNounValue(srcValueTok) : undefined;
        if (srcValue && !this.eventSource) {
          tokens.advance(); // the source marker
          tokens.advance(); // the noun
          this.eventSource = srcValue;
        }
      }
    }

    return true;
  }

  /**
   * Is the stream out of clause? Either genuinely at the end, or facing a
   * boundary token that belongs to the NEXT clause rather than this pattern
   * (a conjunction, or a `then`/`end`-class keyword).
   */
  private atClauseEnd(tokens: TokenStream): boolean {
    const next = tokens.peek();
    if (!next) return true;
    if (next.kind === 'conjunction') return true;
    if (next.kind !== 'keyword') return false;
    const norm = (next.normalized ?? next.value).toLowerCase();
    if (norm === 'then' || norm === 'end') return true;
    return isCuratedEndKeyword(next.value, this.currentProfile?.code ?? '');
  }

  /**
   * Whether a pattern token (literal, or a group starting with a literal) would
   * match the given stream token. Used to keep the event-head source-clause
   * consumption from stealing a marker the pattern explicitly expects.
   */
  private patternTokenWouldMatch(pt: PatternToken | undefined, token: LanguageToken): boolean {
    if (!pt) return false;
    if (pt.type === 'literal') {
      if (this.getMatchType(token, pt.value) !== 'none') return true;
      return !!pt.alternatives?.some(a => this.getMatchType(token, a) !== 'none');
    }
    if (pt.type === 'group') {
      return this.patternTokenWouldMatch(pt.tokens?.[0], token);
    }
    return false;
  }

  // POSITIONAL_KEYWORDS moved to utils/expression-lexicon (exported): the
  // raw-expression join needs the same set, and a second copy would drift.

  /**
   * Positional + DOM-scope keywords that must never fill a `duration` slot (see the
   * guard in matchRoleToken). Superset of POSITIONAL_KEYWORDS plus the scope words
   * (`closest`, `parent`) that lead a locative `in <scope>`.
   */
  private static readonly POSITIONAL_OR_SCOPE_KEYWORDS = new Set([
    'first',
    'last',
    'next',
    'previous',
    'random',
    'closest',
    'parent',
  ]);

  /**
   * Structural block keywords that can never be an event payload on any
   * command (see the event-role guard in matchRoleToken). By NORMALIZED form,
   * so each language's rendered keyword (ja もし, tr eğer, …) is covered.
   * `init` is deliberately absent — `trigger init` is a real corpus event.
   */
  private static readonly STRUCTURAL_NEVER_EVENT = new Set([
    'if',
    'unless',
    'else',
    'end',
    'then',
    // A loop's while-word is a clause head, never an event name: hi's fused
    // `जब तक` normalizes to `while`, and without this row `event-hi-bare`
    // captured it as the handler event, hiding the SOV repeat-while head
    // (`जब तक {condition} दोहराएं`) it introduces.
    'while',
  ]);

  /**
   * Reference bases that can lead a fused-dot property access (`it.value`,
   * `event.detail.message`, `my.innerHTML`). Command verbs are deliberately
   * excluded so `<verb> .class` (toggle .active) is never read as a property
   * path. The corpus uses the English reference forms (they pass through the
   * transformer verbatim); plain identifiers are handled separately.
   */
  private static readonly PROPERTY_ACCESS_BASES = new Set([
    'it',
    'me',
    'you',
    'my',
    'its',
    'your',
    'event',
    'result',
    'target',
    'detail',
    'body',
    'window',
    'document',
    'self',
    'this',
  ]);

  // COMMAND_ACTION_KEYWORDS moved to utils/expression-lexicon (exported) alongside
  // the positional-run recognizer that was its main reader. Aliased here so the two
  // remaining readers below (the fused-dot property guard and the ref-noun guard)
  // keep their existing call shape.
  private static readonly COMMAND_ACTION_KEYWORDS = COMMAND_ACTION_KEYWORDS;

  /**
   * Normalized clause-boundary keywords — sequence/conjunction tokens that close
   * one command and open the next. Tokenizers normalize every language's forms
   * to these (es `entonces`→then, uk `тоді`→then). Used by skipNoiseWords to tell
   * a `the <ref-noun>` patient phrase that ENDS a clause (`the evento entonces …`,
   * safe to skip the leaked article) from one whose ref-noun is followed by a
   * command verb (`the olay çağır …`, must NOT skip — the §7y regression).
   */
  private static readonly CLAUSE_BOUNDARY_KEYWORDS = new Set(['then', 'and', 'or', 'end', 'else']);

  /**
   * Try to match a positional query expression:
   *   <positional> <selector> [<in/from-marker> <source-selector>]
   * e.g. "last <.message/> in #chat", "first <button/> in .modal", "آخر <.message/> في #chat".
   *
   * Only fires when the role begins with a positional keyword, so ordinary
   * roles are untouched. The whole construct is captured as a single
   * expression value (the harness/AST treats positional queries as
   * expressions). The optional source clause consumes exactly one
   * `<marker> <selector>` pair, which is safe because positional queries are
   * terminal in their role (e.g. scroll's only role is the destination).
   */
  private tryMatchPositionalExpression(
    tokens: TokenStream,
    nextPatternToken?: PatternToken
  ): SemanticValue | null {
    // Recognizer lives in expression-lexicon so the raw-expression join shares it
    // — a condition and a then-branch carrying the SAME run (focus-trap authors
    // `last <button/> in .modal` in both) can no longer disagree about `in`.
    // Joins with a plain space here, which is this seam's pre-existing rule.
    //
    // `nextPatternToken` tells the run which marker the enclosing pattern is
    // about to require, so it cannot spend the role marker that terminates it.
    const run = matchPositionalRun(tokens.tokens, tokens.position(), this.currentProfile, t =>
      t === undefined ? false : this.patternTokenWouldMatch(nextPatternToken, t)
    );
    if (!run) return null;
    for (let n = 0; n < run.consumed; n++) tokens.advance();
    return { type: 'expression', raw: run.parts.map(p => p.text).join(' ') } as SemanticValue;
  }

  /**
   * Match a caret-scoped variable read: `^name on <element>`.
   *
   * `^name` is a DOM-scoped variable; `^name on #host` reads it from a specific
   * element rather than walking up from `me`. Gated to `^`-prefixed identifier
   * tokens followed by an `on`-marker and a selector — so the overloaded `on`
   * (event marker / destination marker) is never misread on ordinary commands
   * like `toggle .active on #button` (whose patient is a class selector, not a
   * caret variable). Works across languages: the `on` marker is matched by its
   * normalized form (ar `عند`, tl `kapag`, …) and selectors aren't translated.
   */
  private tryMatchCaretScopeExpression(tokens: TokenStream): SemanticValue | null {
    const token = tokens.peek();
    if (!token || token.kind !== 'identifier' || !token.value.startsWith('^')) {
      return null;
    }

    const onMarker = tokens.peek(1);
    const scope = tokens.peek(2);
    // The `on` marker is normalized to the `destination` role by the tokenizers
    // (it is the destination marker for toggle/add), so accept it by raw value
    // (`on`) or normalized role (`destination`/`on`). `into` normalizes
    // elsewhere, so a `put ^x into #y` is never misread as a scoped read.
    const markerForm = (onMarker?.normalized ?? '').toLowerCase();
    const isOnMarker =
      !!onMarker &&
      (onMarker.value.toLowerCase() === 'on' ||
        markerForm === 'destination' ||
        markerForm === 'on');
    if (!onMarker || !scope || scope.kind !== 'selector' || !isOnMarker) {
      return null;
    }

    tokens.advance(); // ^name
    tokens.advance(); // on-marker
    tokens.advance(); // scope selector
    return { type: 'expression', raw: `${token.value} on ${scope.value}` } as SemanticValue;
  }

  /**
   * Whether a token introduces the owner in a prepositional ("of") possessive.
   * Delegates to the shared expression lexicon, which owns the per-language
   * of-marker table so the possessive matchers and the raw-expression join
   * (`joinExpressionTokens`) cannot disagree about what an of-marker is.
   */
  private isOfPossessiveMarker(token: LanguageToken): boolean {
    return isOfPossessiveMarker(this.currentProfile, token);
  }

  /**
   * The logical connective a token spells, if any: the English word, or the
   * language's own (de `oder`), read through the expression lexicon's
   * connectives, which return an unlisted surface as it is.
   */
  private logicalConnectiveOf(tokens: TokenStream): 'and' | 'or' | 'not' | undefined {
    const token = tokens.peek();
    if (!token) return undefined;
    const language = this.currentProfile?.code ?? 'en';
    const isLogical = (w: string): w is 'and' | 'or' | 'not' =>
      w === 'and' || w === 'or' || w === 'not';
    const word = translateConnective(language, token.value).toLowerCase();
    if (isLogical(word)) return word;
    // A word the connective table leaves out for its other sense, read in
    // place (qu `mana`, also `false`, before an operand is `not`) — unless it
    // is a marker the command owns: sw `na` is `and`, and `hide`'s `with`.
    if (this.commandMarkers().has(token.value.toLowerCase())) return undefined;
    const at = tokens.position();
    const sense = ambiguousSenseOf(language, token, tokens.tokens[at - 1], tokens.tokens[at + 1]);
    return sense && isLogical(sense) ? sense : undefined;
  }

  /**
   * Core's comparison phrases a run takes, as words in the expression parser's
   * match order, with what follows each: an operand (`is less than`), a type
   * name (`is a`), or nothing (`is empty`, `exists`). Left out: `in`, which
   * also marks a query scope and a loop's source; `am`; and `has`/`have`, whose
   * word some languages share with `exists`.
   */
  private static readonly RUN_PHRASES: ReadonlyArray<
    readonly [readonly string[], 'operand' | 'type' | 'none']
  > = [
    ...POSTFIX_PHRASES.map(p => [p.split(' '), 'none'] as const),
    ...TYPE_CHECK_PHRASES.map(p => [p.split(' '), 'type'] as const),
    ...BINARY_PHRASES.filter(p => !/^(in|am|has|have)$|^am /.test(p)).map(
      p => [p.split(' '), 'operand'] as const
    ),
    [['exists'], 'none'],
  ];

  /**
   * Consume the comparison phrase at the stream's head, read word by word as
   * the expression join reads it (ar `هو` after an operand is `is`, es `no` is
   * `not`), and say what follows it; undefined, with nothing consumed, if none.
   */
  private tryConsumeRunPhrase(tokens: TokenStream): 'operand' | 'type' | 'none' | undefined {
    const language = this.currentProfile?.code ?? 'en';
    const all = tokens.tokens;
    const start = tokens.position();
    const words: string[] = [];
    let prevText: string | undefined;
    for (let k = 0; k < 6 && start + k < all.length; k++) {
      const token = all[start + k];
      const word = expressionWordOf(
        language,
        token,
        all[start + k - 1],
        all[start + k + 1],
        prevText
      ).toLowerCase();
      words.push(word);
      prevText = word;
    }
    for (const [phrase, follows] of PatternMatcher.RUN_PHRASES) {
      if (phrase.every((word, i) => words[i] === word)) {
        for (let i = 0; i < phrase.length; i++) tokens.advance();
        return follows;
      }
    }
    return undefined;
  }

  /** Where the slot at the stream's position stands (see SlotContext). */
  private slotAt(
    tokens: TokenStream,
    patternToken: PatternToken & { type: 'role' },
    nextPatternToken: PatternToken | undefined
  ): SlotContext {
    return new SlotContext(
      tokens,
      patternToken,
      this.slotAfterLiteral,
      nextPatternToken,
      this.wouldMatch
    );
  }

  /** A bare word: no digits, no sigil — the only thing a hyphen may join. */
  private static isBareWordToken(token: { value: string } | null | undefined): boolean {
    return !!token && /^[A-Za-z_][A-Za-z0-9_]*$/.test(token.value);
  }

  /**
   * Try to match a multi-token operator-run expression:
   *   <operand> <op> <operand> (<op> <operand>)*
   * where an operand is a parenthesized group (balanced `(`…`)` tokens), a
   * possessive pair (`my value`, `私の 値`), or a single value token.
   * Returns null — leaving the stream untouched — unless an operator DIRECTLY
   * follows the first operand, so ordinary single-token captures never change.
   */
  /**
   * Try to match canonical hyperscript's `beep! <expr>` debug prefix as ONE
   * expression value (`set $x to beep! my value`). Without this fold the value
   * slot captures the bare `beep` (a valid identifier on its own) and the
   * `! <expr>` tail drops as unconsumed — silently WRONG, and invisible to
   * every recall signal because the en REFERENCE truncates identically (the
   * probe measured 18 of 24 languages truncating; only the SOV six, which take
   * the verb-anchoring fall-through into `joinExpressionTokens`, rendered the
   * full value). See HANDOFF_foreign-validity-burndown § "beep!".
   *
   * Gates: the literal `beep` head, a SOURCE-ADJACENT `!` (the same adjacency
   * voucher the join seam's `!`-glue uses — canonical rejects the spaced
   * `beep !`, so adjacency is definitive), and a real operand following (a
   * dangling `beep!` stays a plain single-token capture). A following operator
   * run composes (`beep! my value + 1`) via the same operand consumer.
   */
  private tryMatchBeepPrefixExpression(tokens: TokenStream): SemanticValue | null {
    const head = tokens.peek();
    const bang = tokens.peek(1);
    if (!head || !bang || bang.value !== '!') return null;
    if ((head.normalized ?? head.value).toLowerCase() !== 'beep') return null;
    const adjacent =
      head.position?.end !== undefined &&
      bang.position?.start !== undefined &&
      head.position.end === bang.position.start;
    if (!adjacent) return null;

    const mark = tokens.mark();
    const startIdx = tokens.position();
    tokens.advance(); // beep
    tokens.advance(); // !
    if (!this.tryConsumeRunOperand(tokens)) {
      tokens.reset(mark);
      return null;
    }
    for (;;) {
      const op = tokens.peek();
      if (!op || !RUN_OPERATORS.has(op.value)) break;
      const beforeOp = tokens.mark();
      tokens.advance();
      if (!this.tryConsumeRunOperand(tokens, true)) {
        tokens.reset(beforeOp);
        break;
      }
    }
    const raw = joinExpressionTokens(
      tokens.tokens.slice(startIdx, tokens.position()),
      this.currentProfile
    );
    return { type: 'expression', raw, value: raw } as SemanticValue;
  }

  /**
   * Match a HYPHENATED WORD as one literal: `non-modal`, `aria-hidden`.
   *
   * Every tokenizer here splits on `-`, so `non-modal` arrives as three tokens
   * and `tryMatchOperatorRunExpression` folds it into the expression
   * `non - modal` — a subtraction node, which `OpenCommand`'s
   * `normalized === 'non-modal'` string compare can never match. Left alone,
   * making the role bind at all (the `open` position swap) would have turned a
   * silent default into a WRONG value.
   *
   * Subtraction is told apart by SOURCE ADJACENCY plus operand shape — the same
   * pair of vouchers the `.`- and `!`-glue rules in `joinExpressionTokens` use.
   * A real subtraction either spaces its operator (`a - b`, `count - 1`) or has
   * a non-word operand (`count-1`, `#a-#b`); two source-adjacent BARE WORDS are
   * a hyphenated identifier. Runs of three or more (`data-list-item`) fold too.
   *
   * Runs BEFORE the operator matcher, which would otherwise consume the first
   * `word - word` pair and strand the rest.
   */
  private tryMatchHyphenatedWord(tokens: TokenStream): SemanticValue | null {
    const head = tokens.peek();
    if (!PatternMatcher.isBareWordToken(head)) return null;

    let ahead = 1;
    const parts: string[] = [head!.value];
    for (;;) {
      const hyphen = tokens.peek(ahead);
      const word = tokens.peek(ahead + 1);
      if (
        !hyphen ||
        hyphen.value !== '-' ||
        !PatternMatcher.isBareWordToken(word) ||
        !tokensAdjacent(tokens.peek(ahead - 1), hyphen) ||
        !tokensAdjacent(hyphen, word)
      ) {
        break;
      }
      parts.push(word!.value);
      ahead += 2;
    }

    if (parts.length < 2) return null;
    for (let i = 0; i < ahead; i++) tokens.advance();
    return createLiteral(parts.join('-'), 'string');
  }

  private tryMatchOperatorRunExpression(tokens: TokenStream): SemanticValue | null {
    const mark = tokens.mark();
    const startIdx = tokens.position();

    const head = tokens.peek();
    const negated = !!head && (this.logicalConnectiveOf(tokens) === 'not' || head.value === '-');
    if (!this.tryConsumeRunOperand(tokens)) {
      tokens.reset(mark);
      return null;
    }

    let operands = 1;
    for (;;) {
      const op = tokens.peek();
      if (!op) break;
      const logical = this.logicalConnectiveOf(tokens);
      // A wait's `or` lists its events (`wait for pointermove or pointerup`).
      const joins = (logical === 'and' || logical === 'or') && this.currentRoleCommand !== 'wait';
      const beforeOp = tokens.mark();
      if (joins || RUN_OPERATORS.has(op.value)) {
        tokens.advance();
      } else {
        // A comparison phrase: `is`, `is not greater than`, `matches`, `exists`.
        const follows = this.tryConsumeRunPhrase(tokens);
        if (follows === undefined) break;
        if (follows === 'none') {
          operands++;
          continue;
        }
      }
      if (!this.tryConsumeRunOperand(tokens, true, joins)) {
        // Dangling operator — leave it (and whatever follows) unconsumed.
        tokens.reset(beforeOp);
        break;
      }
      operands++;
    }

    if (operands < 2 && !negated) {
      tokens.reset(mark);
      return null;
    }

    // Join through the shared expression seam rather than a bare `.value` map:
    // the raw is read downstream as English (rendered back out, and parsed by the
    // core expression parser, which only knows English words), so a verbatim join
    // leaked the source language — es `"Hola, " + mi valor` came back byte-identical
    // instead of `"Hola, " + my value`. Also picks up the `.`-glue rule this join
    // never had, so `event.shiftKey` no longer splits into `event .shiftKey`.
    const raw = joinExpressionTokens(
      tokens.tokens.slice(startIdx, tokens.position()),
      this.currentProfile
    );
    return { type: 'expression', raw, value: raw } as SemanticValue;
  }

  /**
   * Separator that joins the two endpoints of a pick range (`0 to 5`). Matched
   * by normalized form (it tokenizes as a keyword). The canonical grammar also
   * allows `..`, but every tokenizer splits it into two bare `.` tokens, so it
   * would need a two-token separator path — deferred (not corpus-exercised; the
   * corpus and the gate row use `to`).
   */
  private static readonly PICK_RANGE_SEPARATORS = new Set(['to']);
  /**
   * Per-language pick-range separator words, matched by NATIVE VALUE (not
   * normalized form): in nearly every language the natural "0 to 5" joiner IS
   * the destination marker (es `a`, ru `в`, ja `から…` aside), so the tokenizer
   * normalizes it to `destination` — a normalized-form match would turn EVERY
   * destination marker into a range separator. Matching the value keeps the
   * table to the audited words, and the fold's gate (pick command + patient
   * role + two-operand shape, see the matchRoleToken call site) is what makes
   * even that collision safe. NEVER route this through the roleMarkers table
   * (same reason). Words are lowercased. The FIRST word of each set is
   * probe-measured from that language's corpus row (or, for the SOV six,
   * chosen in lockstep with its sovPickRangeRule render) and is exercised by
   * the 24-language table in test/pick-command.test.ts; any SECOND word is an
   * untested input-tolerance alternate (the natural "up to/until" a native
   * writer might use — de `bis`, ru/uk `до`, th `ถึง`…). Don't build on the
   * alternates without probing them first.
   * (docs-internal/HANDOFF_pick-text-range-arc3.md).
   */
  private static readonly PICK_RANGE_SEPARATORS_BY_LANG: Readonly<
    Record<string, ReadonlySet<string>>
  > = {
    ar: new Set(['إلى']),
    bn: new Set(['থেকে']),
    de: new Set(['zu', 'bis']),
    es: new Set(['a']),
    fr: new Set(['à']),
    he: new Set(['על', 'עד']),
    hi: new Set(['से']),
    id: new Set(['ke']),
    it: new Set(['in', 'a']),
    ja: new Set(['から']),
    ko: new Set(['부터']),
    ms: new Set(['ke']),
    pl: new Set(['do']),
    pt: new Set(['para', 'a']),
    qu: new Set(['kama']),
    ru: new Set(['в', 'до']),
    sw: new Set(['kwa', 'hadi']),
    th: new Set(['ใน', 'ถึง']),
    tl: new Set(['sa']),
    tr: new Set(['ile']),
    uk: new Set(['в', 'до']),
    vi: new Set(['vào', 'đến']),
    zh: new Set(['到']),
  };

  /** The word this language joins two pick-range endpoints with. */
  static rangeSeparatorFor(language: string): string | undefined {
    const [first] = PatternMatcher.PICK_RANGE_SEPARATORS_BY_LANG[language] ?? [];
    return first;
  }

  /**
   * Is this token the range separator between two pick-range endpoints?
   * English `to` matches by normalized form (it tokenizes as a keyword in
   * every language and en normalizes it to itself); the per-language native
   * words match by value — see PICK_RANGE_SEPARATORS_BY_LANG for why the two
   * lookups differ.
   */
  private isPickRangeSeparator(token: LanguageToken): boolean {
    const norm = (token.normalized ?? token.value).toLowerCase();
    if (PatternMatcher.PICK_RANGE_SEPARATORS.has(norm)) return true;
    const lang = this.currentProfile?.code;
    if (!lang) return false;
    return (
      PatternMatcher.PICK_RANGE_SEPARATORS_BY_LANG[lang]?.has(token.value.toLowerCase()) ?? false
    );
  }
  /** Trailing range-mode keywords: `pick characters 0 to 5 inclusive of #note`. */
  private static readonly PICK_RANGE_MODES = new Set(['inclusive', 'exclusive']);

  /**
   * Try to match a pick-range expression: `<start> to <end> [inclusive
   * |exclusive]`, where each endpoint is a numeric/identifier value or the range
   * keyword `start`/`end`. Returns one expression value carrying the canonical
   * surface (`0 to 5`, `0 to 5 inclusive`) so the pick pattern's trailing
   * `of {source}` still matches and the value renders verbatim. Stream is left
   * untouched (reset) on any non-match — including a lone endpoint with no
   * separator, so a foreign `pick {patient}` slot that captures a single value
   * (es `characters`) is unaffected. See the call site in matchRoleToken.
   */
  private tryMatchPickRangeExpression(tokens: TokenStream): SemanticValue | null {
    const mark = tokens.mark();
    const startIdx = tokens.position();

    if (!this.tryConsumePickRangeOperand(tokens)) {
      tokens.reset(mark);
      return null;
    }

    const sep = tokens.peek();
    if (!sep || !this.isPickRangeSeparator(sep)) {
      tokens.reset(mark);
      return null;
    }
    tokens.advance();

    if (!this.tryConsumePickRangeOperand(tokens)) {
      tokens.reset(mark);
      return null;
    }

    // Optional trailing range mode (`inclusive`/`exclusive`).
    const mode = tokens.peek();
    if (
      mode &&
      PatternMatcher.PICK_RANGE_MODES.has((mode.normalized ?? mode.value).toLowerCase())
    ) {
      tokens.advance();
    }

    // Fold-time ENGLISH normalization: synthesize `<a> to <b> [inclusive|
    // exclusive]` rather than joining the surface tokens. The folded value is
    // then canonical English for every language (es `0 a 5` → `0 to 5`), so
    // the pick mapper's hard-coded `/\s+to\s+/` split and the en render work
    // unchanged — one normalization site instead of two per-language ones.
    // Keyword endpoints/modes emit their normalized form (native `start`/`end`
    // /mode words → English); literals and identifiers emit their value, which
    // for en makes the output byte-identical to the old token join (the
    // round-trip suite in test/pick-command.test.ts holds this invariant).
    const folded = tokens.tokens.slice(startIdx, tokens.position());
    const [a, , b, m] = folded;
    const endpointText = (t: LanguageToken): string =>
      t.kind === 'keyword' ? (t.normalized ?? t.value) : t.value;
    let raw = `${endpointText(a!)} to ${endpointText(b!)}`;
    if (m) raw += ` ${(m.normalized ?? m.value).toLowerCase()}`;
    return { type: 'expression', raw, value: raw } as SemanticValue;
  }

  /**
   * Consume one endpoint of a pick range: a single literal/identifier value, or
   * the range keyword `start`/`end` (by value). Returns false (stream untouched)
   * if the upcoming token is not an endpoint — notably a bare separator or the
   * `of`/`from` source marker, which belong to the surrounding pattern.
   */
  private tryConsumePickRangeOperand(tokens: TokenStream): boolean {
    const token = tokens.peek();
    if (!token) return false;
    const norm = (token.normalized ?? token.value).toLowerCase();
    if (this.isPickRangeSeparator(token)) return false;
    const isEndpointKeyword = norm === 'start' || norm === 'end';
    if (
      token.kind === 'literal' ||
      token.kind === 'identifier' ||
      (token.kind === 'keyword' && isEndpointKeyword)
    ) {
      tokens.advance();
      return true;
    }
    return false;
  }

  /**
   * Consume one operand of an operator run. Returns false (stream untouched)
   * if the upcoming tokens do not form an operand.
   */
  private tryConsumeRunOperand(
    tokens: TokenStream,
    afterOperator = false,
    afterConnective = false
  ): boolean {
    const token = tokens.peek();
    if (!token) return false;

    // `not <operand>`: the prefix belongs to its operand (`p and not q`).
    if (this.logicalConnectiveOf(tokens) === 'not') {
      const mark = tokens.mark();
      tokens.advance();
      // Unless the word is a variable spelled like `not` (C12).
      if (notWordIsVariable(tokens.peek() ?? undefined, tokens.peek(1) ?? undefined)) {
        tokens.reset(mark);
        return false;
      }
      if (this.tryConsumeRunOperand(tokens, true, afterConnective)) return true;
      tokens.reset(mark);
      return false;
    }

    // A unary minus belongs to its operand too (`-#a's textContent`): where
    // an operand may start, a `-` is never the binary one.
    if (token.value === '-') {
      const mark = tokens.mark();
      tokens.advance();
      if (this.tryConsumeRunOperand(tokens, true, afterConnective)) return true;
      tokens.reset(mark);
      return false;
    }

    // After `and`/`or`, a command verb starts the next command (semantic reads
    // `put 1 into #a and put 2 into #c` as two commands), never an operand.
    if (afterConnective && namesCommand(token)) return false;

    // A particle beside an operator is an operand (C10: particleIsOperand).
    if (particleIsOperand(token, afterOperator, tokens.peek(1) ?? undefined)) {
      tokens.advance();
      return true;
    }

    // Parenthesized group: `(` … matching `)` (parens tokenize as standalone
    // identifier tokens; role markers INSIDE the parens — hi `के_रूप_में`'s में —
    // are opaque to the balance count, which is the point).
    if (token.value === '(') {
      const mark = tokens.mark();
      let depth = 0;
      while (!tokens.isAtEnd()) {
        const t = tokens.advance();
        if (t.value === '(') depth++;
        else if (t.value === ')') {
          depth--;
          if (depth === 0) return true;
        }
      }
      tokens.reset(mark); // unbalanced
      return false;
    }

    // Possessive pair (`my value`, `私の 値`, `mi valor`).
    if (this.tryMatchPossessiveExpression(tokens)) return true;

    // `the` before an operand is an article, as upstream's unaryExpression reads
    // it. The role capture drops it before a whole value (skipNoiseWords), but
    // nothing did after an operator: `"x" + the textContent of #d1`.
    if (token.value.toLowerCase() === 'the') {
      const mark = tokens.mark();
      tokens.advance();
      if (this.tryConsumeRunOperand(tokens, afterOperator, afterConnective)) return true;
      tokens.reset(mark);
    }

    // A property path, through the matchers the role capture uses for a whole
    // value: `#d1's textContent`, `textContent of #d1`, `#d1.textContent`, and
    // each language's surface for them (`textContent de #d1`,
    // `#d1のtextContent`). Without them a value that started with a path was
    // never a run: the path was captured alone and the rest left over, so `put
    // #d1's textContent + "x" into #out` lost its whole `put`. A call is not an
    // operand here. The run's raw spaces its parentheses, so where a set's value
    // follows its destination with no marker between (it `impostare in
    // #t.innerText ( … ) * 2`), a path's arguments and the value look the same.
    if (
      this.tryMatchOfPossessiveExpression(tokens) ||
      this.tryMatchPossessiveSelectorExpression(tokens) ||
      this.tryMatchPropertyAccessExpression(tokens, true) ||
      this.tryMatchSelectorPropertyExpression(tokens)
    ) {
      return true;
    }

    // Single value token. Particles/conjunctions/punctuation are never
    // operands (they belong to the surrounding pattern), and neither is a
    // block's own word (sw `mwisho`, `end`, after `inafanana`, matches) —
    // unless it stands where a value does (C3: `1 + fin`, es `fin`, a
    // variable).
    const norm = (token.normalized ?? '').toLowerCase();
    if (
      (token.kind === 'literal' ||
        token.kind === 'identifier' ||
        token.kind === 'selector' ||
        token.kind === 'keyword') &&
      !RUN_OPERATORS.has(token.value) &&
      !['then', 'else'].includes(norm) &&
      (norm !== 'end' || this.endWordIsValueAt(tokens)) &&
      token.value !== ')'
    ) {
      tokens.advance();
      return true;
    }
    return false;
  }

  /** C3: is the end word at the stream's position a value (endWordIsValue)? */
  private endWordIsValueAt(tokens: TokenStream): boolean {
    return endWordIsValue(
      endWordPlace(tokens.tokens, tokens.position()),
      this.currentProfile?.code ?? ''
    );
  }

  /**
   * Whether a token is a possessive pronoun in the active profile (`my`, `لي`,
   * `yangu`) rather than a property name.
   */
  private isPossessorToken(token: { value: string; normalized?: string }): boolean {
    if (!this.currentProfile) return false;
    for (const cand of [token.value, token.normalized].filter(Boolean) as string[]) {
      if (
        getPossessiveReference(this.currentProfile, cand) ??
        getPossessiveReference(this.currentProfile, cand.toLowerCase())
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Whether a bare identifier can head a possessive property phrase — `valor de
   * #picker`, `textContent von #status`.
   *
   * Sigil-led identifiers are values, not properties: `$x`/`:x` tokenize as
   * references and `@x`/`*x`/`.x`/`#x` as selectors, so `$total de #x` must never
   * fold into a property-path. Structural keywords, role-marker concepts and
   * possessive pronouns belong to other matchers and are never property names.
   */
  private isBareWordPropertyHead(token: LanguageToken): boolean {
    if (token.kind !== 'identifier') return false;
    if (/^[$:@#.*]/.test(token.value)) return false;
    if (this.isStructuralKeyword(token.value)) return false;
    if (token.normalized && this.isStructuralKeyword(token.normalized)) return false;
    if (token.normalized && this.isRoleMarkerConcept(token.normalized)) return false;
    return !this.isPossessorToken(token);
  }

  /**
   * Normalize a captured possessive property surface to its English DOM-property
   * name (`valor`/`値`/`قيمة` → `value`). Only the property HEAD is translated —
   * chained member access (`.style.display`) and method calls are code and stay
   * verbatim. An unlisted surface (an English property, a `*`/`@` selector) is
   * returned unchanged. Keeps foreign→English renders canonical and makes the
   * AST-execution path read the real DOM property. See
   * `./utils/expression-lexicon.ts`.
   */
  private toEnglishProperty(surface: string): string {
    return this.currentProfile ? translatePropertyName(this.currentProfile.code, surface) : surface;
  }

  /**
   * Whether a role slot opts into the property-path matchers.
   *
   * A typed slot answers for itself. An UNTYPED slot inherits its schema role's
   * answer: the fused `<cmd>-event-*` patterns (event-handlers-vso/-sov) emit
   * their command's role slots with no `expectedTypes`, so `al clic incrementar
   * textContent de #out` captured the bare property word and stranded the owner,
   * while the same command outside a handler (the generated pattern, typed from
   * the schema) captured the whole path. Only the opt-in is inherited — the
   * untyped slot still accepts whatever it accepted before.
   */
  private roleAcceptsPropertyPath(patternToken: PatternToken): boolean {
    if (patternToken.type !== 'role') return false;
    if (patternToken.expectedTypes) return patternToken.expectedTypes.includes('property-path');
    const command = this.currentRoleCommand;
    if (!command) return false;
    const schema = (commandSchemas as Record<string, CommandSchema | undefined>)[command];
    const spec = schema?.roles.find(r => r.role === patternToken.role);
    return spec?.expectedTypes?.includes('property-path') ?? false;
  }

  /**
   * Try to match a prepositional "of" possessive:
   *   <property> <of-marker> <owner-selector>
   * e.g. "*--primary-color of #theme" → property-path(#theme, *--primary-color),
   * or "valor de #picker" → property-path(#picker, valor).
   *
   * This is the surface the i18n transformer emits for "set the X of #y to Z"
   * across languages (`set #y's X` would be the alternative, but the transformer
   * uses the `of` form). Only called for property-path roles (see matchRoleToken).
   */
  private tryMatchOfPossessiveExpression(
    tokens: TokenStream,
    nextPatternToken?: PatternToken
  ): SemanticValue | null {
    const property = tokens.peek();
    if (!property) return null;
    // A selector head is the historical form (`*--primary-color of #theme`). A
    // bare-word head is the property-first render the transformer emits for
    // `#picker's value` in es/pt/fr/de/id/sw/ar (`valor de #picker`), which
    // otherwise captured the property word alone as an expression and dropped
    // the owner. Selector-first languages (en `#picker's value`, ja `#pickerの
    // 値`) never reach here: their owner check below rejects the property word.
    // A selector head is a style or an attribute: a query (`<li/>`, `#id`,
    // `.class`) is never a property, and tr's genitive `in` read `<li/> in
    // #list`, a scoped query, as `#list's <li/>`.
    const head =
      property.kind === 'selector'
        ? /^[*@]/.test(property.value)
        : this.isBareWordPropertyHead(property);
    if (!head) return null;

    const start = tokens.position();
    const mark = tokens.mark();
    tokens.advance();

    const marker = tokens.peek();
    if (!marker || !this.isOfPossessiveMarker(marker)) {
      tokens.reset(mark);
      return null;
    }
    tokens.advance();

    // English `of the previous <output/>`: the article is noise before an owner.
    if (tokens.peek()?.value.toLowerCase() === 'the') tokens.advance();

    // A POSITIONAL owner — `the textContent of the previous <output/>`, the
    // book's counter (Hypermedia Systems ch. 9). The run is captured the way
    // tryMatchPositionalExpression captures it for any other role (one
    // expression, English positional keyword), so the object renders and
    // re-parses exactly as a bare positional role value does.
    const positional = matchPositionalRun(
      tokens.tokens,
      tokens.position(),
      this.currentProfile,
      t => (t === undefined ? false : this.patternTokenWouldMatch(nextPatternToken, t))
    );
    if (positional) {
      for (let n = 0; n < positional.consumed; n++) tokens.advance();
      return createPropertyPath(
        { type: 'expression', raw: positional.parts.map(p => p.text).join(' ') },
        this.toEnglishProperty(property.value),
        'possessive'
      );
    }

    const owner = tokens.peek();
    if (!owner || owner.kind !== 'selector' || isSigilProperty(owner.value)) {
      tokens.reset(mark);
      // A chain, each link the next one's owner: tr renders `#a's
      // textContent's length` as `length nin textContent nin #a`. This read
      // one link, so the value kept only `#a's textContent`. Read it whole, as
      // the value join does — unless its first marker is one the pattern wants
      // next: it says an increment's `by` with `di`, its `of` too, and
      // `incrementare i di textContent di #a` is `increment i by #a's
      // textContent`.
      if (this.patternTokenWouldMatch(nextPatternToken, marker)) {
        return null;
      }
      const language = this.currentProfile?.code ?? 'en';
      const end = ofChainEnd(tokens.tokens, start, this.currentProfile, language);
      if (end === undefined) return null;
      while (tokens.position() <= end) tokens.advance();
      const raw = joinExpressionTokens(tokens.tokens.slice(start, end + 1), this.currentProfile);
      return { type: 'expression', raw, value: raw } as SemanticValue;
    }
    tokens.advance();

    // "X of #y" means the X property of #y → property-path(object: #y, property: X).
    // An `of`-phrase is a POSSESSIVE surface, not a member access.
    //
    // The owner guard above is what keeps that reading honest. OF_POSSESSIVE_MARKERS
    // lists the head-final clitics (ja の, ko 의, zh 的, bn র, hi का) alongside the
    // genuine "of" linkers, because the i18n transformer emitted property-first in
    // every language and this matcher was built to read that. In a clitic language
    // `A の B` actually means "A's B", so on the semantic renderer's (correct)
    // owner-first surface this matcher folds the pair INVERTED. A `*`/`@` sigil can
    // never be an OWNER, so refusing one there is enough to tell the two apart:
    // `*background-color ของ #theme` (i18n, property-first) still folds here, while
    // `#themeの*background-color` falls through to the selector-possessive matcher,
    // which reads it owner-first and correctly.
    return createPropertyPath(
      createSelector(owner.value),
      this.toEnglishProperty(property.value),
      'possessive'
    );
  }

  /**
   * Owner-first possessive whose owner is a positional run:
   *   <positional> <selector> <possessive-marker> <property>
   * e.g. ja `前 <output/> の textContent` → property-path(previous <output/>,
   * textContent). Only for the languages whose possessive marker sits BETWEEN
   * owner and property and reads owner-first; th's `ของ` reads property-first
   * and goes through the of-possessive matcher instead.
   */
  private tryMatchPositionalPossessiveExpression(
    tokens: TokenStream,
    nextPatternToken?: PatternToken
  ): SemanticValue | null {
    const profile = this.currentProfile;
    const marker = profile?.possessive?.marker;
    if (!profile || !marker || profile.possessive?.markerPosition !== 'between') return null;
    if (profile.code === 'th') return null;

    const run = matchPositionalRun(tokens.tokens, tokens.position(), profile, t =>
      t === undefined ? false : this.patternTokenWouldMatch(nextPatternToken, t)
    );
    if (!run) return null;
    const markerTok = tokens.peek(run.consumed);
    const property = tokens.peek(run.consumed + 1);
    if (!markerTok || markerTok.value !== marker) return null;
    // Same property admission as the selector-possessive matcher's profile-marker
    // branch: a bare word, a `*` style property, or a keyword the language's
    // property table vouches for (vi `giá trị`). A command verb is none of them.
    const propertyOk =
      !!property &&
      (this.isBareWordPropertyHead(property) ||
        (property.kind === 'selector' && property.value.startsWith('*')) ||
        (property.kind === 'keyword' && isKnownPropertySurface(profile.code, property.value)));
    if (!property || !propertyOk) return null;

    for (let n = 0; n < run.consumed + 2; n++) tokens.advance();
    return createPropertyPath(
      { type: 'expression', raw: run.parts.map(p => p.text).join(' ') },
      this.toEnglishProperty(property.value),
      'possessive'
    );
  }

  /**
   * Try to match a possessive expression like 'my value' or 'its innerHTML'.
   * Returns the PropertyPathValue if matched, or null if not.
   */
  private tryMatchPossessiveExpression(tokens: TokenStream): SemanticValue | null {
    const token = tokens.peek();
    if (!token) return null;

    // Use profile-based possessive keyword lookup
    if (!this.currentProfile) return null;

    // Look up the possessive keyword by BOTH the native value and the normalized
    // form. Profiles key `possessive.keywords` by the NATIVE word (EN 'my',
    // AR 'لي', TL 'aking', HE 'שלי', KO '내'), but most non-English tokenizers
    // normalize that word to its English base ('لي' → normalized 'me'). Looking
    // up only the normalized form misses every native-keyed profile, so the
    // possessive value-filler ('my value', 'لي قيمة') never matches outside
    // English. Trying the native value first makes the lookup language-agnostic.
    const candidates = [token.value, token.normalized].filter(Boolean) as string[];
    let baseRef: string | undefined;
    for (const candidate of candidates) {
      baseRef =
        getPossessiveReference(this.currentProfile, candidate) ??
        getPossessiveReference(this.currentProfile, candidate.toLowerCase());
      if (baseRef) break;
    }

    if (!baseRef) return null;

    // We have a possessive keyword, look ahead for property name
    const mark = tokens.mark();
    tokens.advance();

    // Skip a possessive CONNECTOR for multi-word possessor-first constructions
    // (Indonesian `saya punya *background` = "I have *background" = "my
    // *background"). The connector sits between the possessor keyword and the
    // property; without skipping it, `punya` is read as the property and the
    // whole set body fails to parse (id set-style/set-text).
    const connectors = this.currentProfile.possessive?.connectors;
    if (connectors && connectors.length > 0) {
      const next = tokens.peek();
      if (next) {
        const nv = next.value.toLowerCase();
        const nn = (next.normalized ?? '').toLowerCase();
        if (connectors.some(c => c.toLowerCase() === nv || c.toLowerCase() === nn)) {
          tokens.advance();
        }
      }
    }

    const propertyToken = tokens.peek();
    if (!propertyToken) {
      // Just the possessive keyword, no property - revert
      tokens.reset(mark);
      return null;
    }

    // Property should be an identifier, keyword (not structural), or selector (for style/dot/attr)
    // Examples: "my value", "my innerHTML", "my *background", "my *opacity", "my @data-count"
    // Also handles dot-property access: "my.textContent" tokenized as "my" + ".textContent"
    // Structural keywords are checked by NORMALIZED form too — non-English
    // structural words (ms kemudian→then, tamat→end) otherwise read as a
    // property ("its then"), forming a phantom property-path whose type check
    // fails the whole pattern (ms `letak 'X' ke ia kemudian …` dropped the put).
    //
    // The same trap exists for ROLE-MARKER keywords whose normalized form is a
    // role *concept* rather than an English preposition surface word: ms `ke`
    // (into) normalizes to `destination`, `dari` (from) to `source`, `pada` (to)
    // to `destination`. `isStructuralKeyword` only knows the English surface
    // prepositions (`into`/`to`/`from`), so a possessive pronoun directly
    // followed by such a marker — `letak ia ke #container` (put it into X),
    // `letak saya ke …` (put me into …) — read `ia ke` as the phantom possessive
    // `it.ke`, the literal `ke` match then failed, and the whole put dropped
    // (the §10 "ms put-with-`ia`" bug). A marker concept is never a real DOM
    // property name, so reject it as a possessive property head.
    //
    // A positional `at end of` connective (ms `di`/`tamat`/`daripada`, zh
    // `在`/`结束`/`的`) tokenizes as a bare identifier with NO normalized concept,
    // so the guards above miss it. `letak ia di tamat daripada badan` (put it at
    // end of body, make-toast's attaching put) read `ia di` as the phantom
    // possessive `it.di`, the literal `di` then failed and the put dropped. Bail
    // so `ia` stays a bare patient and the at-end pattern matches.
    if (isAtEndConnective(this.currentProfile.code, propertyToken.value)) {
      tokens.reset(mark);
      return null;
    }
    if (
      propertyToken.kind === 'identifier' ||
      (propertyToken.kind === 'keyword' &&
        !this.isStructuralKeyword(propertyToken.value) &&
        !(propertyToken.normalized && this.isStructuralKeyword(propertyToken.normalized)) &&
        !(propertyToken.normalized && this.isRoleMarkerConcept(propertyToken.normalized)) &&
        // A COMMAND VERB is never a property name either: ms `… ke ia ukur y`
        // ("… to it" + "measure y") read `ia ukur` as the phantom possessive
        // `it.measure`, swallowing the next clause's verb — set-ms-full then
        // captured a null property-path patient and the measure vanished
        // (behavior-draggable/resizable ms, the corpus's one remaining
        // multiset deficit). Normalized-form-gated, so en (whose keywords
        // carry no normalized) keeps its reference behavior byte-identical.
        !(
          propertyToken.normalized &&
          PatternMatcher.COMMAND_ACTION_KEYWORDS.has(propertyToken.normalized.toLowerCase())
        )) ||
      (propertyToken.kind === 'selector' && propertyToken.value.startsWith('*')) ||
      (propertyToken.kind === 'selector' && propertyToken.value.startsWith('@')) ||
      (propertyToken.kind === 'selector' &&
        propertyToken.value.startsWith('.') &&
        /^\.[a-zA-Z_]\w*/.test(propertyToken.value))
    ) {
      tokens.advance();

      // For dot-property selectors (.textContent), strip the leading dot
      let propertyName = propertyToken.value;
      if (
        propertyToken.kind === 'selector' &&
        propertyName.startsWith('.') &&
        /^\.[a-zA-Z_]\w*/.test(propertyName)
      ) {
        propertyName = propertyName.substring(1);
      }

      // Normalize the property HEAD to English (`valor`/`値` → `value`) before any
      // chained member access is folded on. `my valor` renders `my value`; `my
      // valor.length` renders `my value.length` (the chained `.length` is code).
      propertyName = this.toEnglishProperty(propertyName);

      // Consume chained dot-property access (.parentElement.style.display),
      // including optional-chaining links: the tokenizer splits `?.prop` into
      // `?` + `.prop`, and stopping at the `?` left the chain HALF-consumed —
      // property-path(me, "?.dataset") with `?.customValue` stranded, so the
      // verb-final SOV patterns could never complete through to their patient
      // particle and the row fell to a bare `?.customValue` expression
      // (optional-chaining-possessive, R1 Family D). A `?` is only folded
      // when a `.prop` selector immediately follows, so a ternary after a
      // possessive value is untouched.
      let chainedProps = propertyName;
      for (;;) {
        const nxt = tokens.peek();
        if (
          nxt?.kind === 'selector' &&
          nxt.value.startsWith('.') &&
          /^\.[a-zA-Z_]\w*/.test(nxt.value)
        ) {
          chainedProps += nxt.value; // keep the dots for chaining
          tokens.advance();
          continue;
        }
        if (nxt?.value === '?') {
          const after = tokens.peek(1);
          if (
            after?.kind === 'selector' &&
            after.value.startsWith('.') &&
            /^\.[a-zA-Z_]\w*/.test(after.value)
          ) {
            chainedProps += `?${after.value}`;
            tokens.advance();
            tokens.advance();
            continue;
          }
        }
        break;
      }

      // Check for a trailing method call: chain + '(' [args...] ')'
      // (e.g., my.value.toUpperCase(), my.getAttribute("data-id")). The tokenizer
      // may emit the call as one token (`("data-id")`) or split it into `(` / args
      // / `)` (kinds vary: identifier/punctuation/literal), so consume by value
      // until the parentheses balance rather than relying on a single-token form.
      const afterChain = tokens.peek();
      if (afterChain && afterChain.value.startsWith('(')) {
        let call = '';
        let depth = 0;
        let guard = 0;
        while (!tokens.isAtEnd() && guard++ < PatternMatcher.MAX_METHOD_ARGS + 2) {
          const t = tokens.peek();
          if (!t) break;
          call += t.value;
          tokens.advance();
          for (const ch of t.value) {
            if (ch === '(') depth++;
            else if (ch === ')') depth--;
          }
          if (depth <= 0) break;
        }
        chainedProps += call;
      }

      // Create property-path: my value -> { object: me, property: 'value' }
      // baseRef from getPossessiveReference is always a valid reference ('me', 'you', 'it', etc.)
      return createPropertyPath(
        createReference(baseRef as ReferenceValue['value']),
        chainedProps,
        'possessive'
      );
    }

    // Not a valid property, revert
    tokens.reset(mark);
    return null;
  }

  /**
   * Match a POST-NOMINAL possessive expression: `<property> <possessor>`.
   *
   * The pre-nominal {@link tryMatchPossessiveExpression} handles possessor-first
   * order (`my value`, `لي قيمة`). Languages whose possessive `markerPosition` is
   * `after-object` put the possessor AFTER the property (ar `textContent لي`,
   * id `textContent saya`, sw `textContent yangu`), so the pre-nominal matcher
   * never fires — `set my textContent to "x"` failed to parse (ar/id/sw) or parsed
   * lossily with the possessor dropped (ru/pl/uk). This recovers the property-path
   * for that order. Gated to `after-object` profiles, so possessor-first languages
   * are untouched.
   */
  private tryMatchPostNominalPossessiveExpression(tokens: TokenStream): SemanticValue | null {
    if (!this.currentProfile) return null;
    if (this.currentProfile.possessive?.markerPosition !== 'after-object') return null;

    const propertyToken = tokens.peek();
    if (!propertyToken) return null;

    // The head must be a plausible property name — a bare identifier or a dot /
    // `*` / `@` property selector — and must not itself be a possessor, structural
    // keyword, or role-marker concept (those are handled elsewhere / are not
    // properties).
    const isDotSelector =
      propertyToken.kind === 'selector' &&
      propertyToken.value.startsWith('.') &&
      /^\.[a-zA-Z_]\w*/.test(propertyToken.value);
    const isPropHead =
      (propertyToken.kind === 'identifier' &&
        !this.isStructuralKeyword(propertyToken.value) &&
        !(propertyToken.normalized && this.isStructuralKeyword(propertyToken.normalized)) &&
        !(propertyToken.normalized && this.isRoleMarkerConcept(propertyToken.normalized))) ||
      (propertyToken.kind === 'selector' &&
        (propertyToken.value.startsWith('*') || propertyToken.value.startsWith('@'))) ||
      isDotSelector;
    if (!isPropHead || this.isPossessorToken(propertyToken)) return null;

    const mark = tokens.mark();

    let propertyName = propertyToken.value;
    if (isDotSelector) propertyName = propertyName.substring(1);
    tokens.advance();

    // Normalize the property HEAD to English before folding chained members.
    propertyName = this.toEnglishProperty(propertyName);

    // Consume chained dot-property access (`.style.display`) before the possessor.
    let chainedProps = propertyName;
    while (
      tokens.peek()?.kind === 'selector' &&
      tokens.peek()!.value.startsWith('.') &&
      /^\.[a-zA-Z_]\w*/.test(tokens.peek()!.value)
    ) {
      chainedProps += tokens.peek()!.value;
      tokens.advance();
    }

    // Now require the possessor keyword.
    const possessorToken = tokens.peek();
    if (!possessorToken) {
      tokens.reset(mark);
      return null;
    }
    let baseRef: string | undefined;
    for (const cand of [possessorToken.value, possessorToken.normalized].filter(
      Boolean
    ) as string[]) {
      baseRef =
        getPossessiveReference(this.currentProfile, cand) ??
        getPossessiveReference(this.currentProfile, cand.toLowerCase());
      if (baseRef) break;
    }
    if (!baseRef) {
      tokens.reset(mark);
      return null;
    }
    // The renderer writes the possessor first in every language, so where
    // the slot runs straight into the next role (pl/ru/uk `set`: `ustaw do x
    // mój id`, set x to my id) a possessor that owns the word after it is
    // that word's: read post-nominally, `x mój` took the `my` and left `id`.
    const afterPossessor = tokens.mark();
    if (this.tryMatchPossessiveExpression(tokens)) {
      tokens.reset(mark);
      return null;
    }
    tokens.reset(afterPossessor);
    tokens.advance();

    return createPropertyPath(
      createReference(baseRef as ReferenceValue['value']),
      chainedProps,
      'possessive'
    );
  }

  /**
   * Check if a keyword is a structural keyword (preposition, control flow, etc.)
   * that shouldn't be consumed as a property name.
   */
  private isStructuralKeyword(value: string): boolean {
    const structural = new Set([
      // Prepositions
      'into',
      'in',
      'to',
      'from',
      'at',
      'by',
      'with',
      'without',
      'before',
      'after',
      'of',
      'as',
      'on',
      // Control flow
      'then',
      'end',
      'else',
      'if',
      'repeat',
      'while',
      'for',
      // Commands (shouldn't be property names)
      'toggle',
      'add',
      'remove',
      'put',
      'set',
      'show',
      'hide',
      'increment',
      'decrement',
      'send',
      'trigger',
      'call',
    ]);
    return structural.has(value.toLowerCase());
  }

  /**
   * Whether a NORMALIZED token form is a role-marker *concept* (the abstract
   * role name a non-English marker keyword normalizes to, e.g. ms `ke`→
   * `destination`, `dari`→`source`). These are markers, never DOM property
   * names — so a possessive property head normalizing to one is a mis-read
   * (`letak ia ke …` = "put it into …", not "put it's `ke`"). Only checked
   * against the `normalized` form: a real English property surface word
   * (`value`, `style`) tokenizes as a bare identifier with no normalized concept,
   * so this never blocks a genuine `my value` / `its style` possessive.
   */
  private isRoleMarkerConcept(normalized: string): boolean {
    return ROLE_MARKER_CONCEPTS.has(normalized.toLowerCase());
  }

  /**
   * Does `token` look like an EVENT name for the `event` role? Events are bare
   * identifiers / known event keywords (click, keyup, custom `myEvent`), never
   * selectors, URLs, method calls (`fn()`), or string/number literals.
   * Value-based so it works for any language's native event words (no special
   * chars). See the event-anchor guard in matchRoleToken and
   * docs-internal/HANDOFF-sov-event-anchor.md.
   */
  /** Upstream's hide/show strategies (`hide me with opacity`), never event names. */
  private static readonly VISIBILITY_STRATEGIES: ReadonlySet<string> = new Set([
    'display',
    'visibility',
    'opacity',
    'twdisplay',
    'twvisibility',
    'twopacity',
  ]);

  private static tokenLooksLikeEvent(token: LanguageToken): boolean {
    // Selectors (.class/#id/<tag/>/[attr]) and URLs are never events.
    if (token.kind === 'selector' || (token.kind as string) === 'url') return false;
    // Quoted strings / numbers tokenize as `literal`; events never do.
    if (token.kind === 'literal') return false;
    const v = token.value;
    // URL/path (`/api/data`) or selector/attr punctuation (`#id`, `*prop`, `@attr`):
    // never an event head. The KEY case is the fronted fetch URL — `fetch /api/data`
    // → SOV `/api/data को लाएं`, where the bare-event pattern (priority 80, Stage 1)
    // would otherwise anchor `/api/data` as the event before the command-stage
    // fetch pattern (Stage 2) runs. Rejecting it lets the fetch command pattern win.
    // Parens are deliberately NOT rejected: a fronted `myFunction()` mis-anchor is
    // already outranked by the per-command patient-first pattern (priority 145 >
    // 80). (The reactive `when (<expr>) changes` head, which once relied on this
    // to capture its expression in the `event` role, is parsed structurally now —
    // block-parser `locateReactiveWhenHead` — and never reaches this guard.)
    if (/[/#@*]/.test(v)) return false;
    if (v.startsWith('"') || v.startsWith("'") || v.startsWith('`')) return false;
    // A body-bearing block keyword fronting the input (`live`/`socket`/… → SOV
    // `लाइव …`) is a command, not an event: the bare-event pattern (priority 80,
    // Stage 1) would otherwise anchor it as the handler event before the command
    // stage's bareKeyword pattern (Stage 2) runs, dropping the block action (hi
    // live-derived-value/live-multiple-deps parsed as a bare `on` + `put`).
    // Rejecting it lets the `live`/`socket` command pattern win at Stage 2.
    const norm = token.normalized?.toLowerCase();
    if (norm && BAREKEYWORD_BLOCK_ACTIONS.has(norm)) return false;
    // A personal REFERENCE (me/it/you) is never an event name. The renderer
    // fronts a command's reference destination in the postpositional languages
    // (`add .highlight to me` → bn `আমি তে .highlight কে যোগ`), and the
    // event-stage `{event} <marker>` pattern otherwise reads `আমি তে` as
    // `on me` — the junk handler swallows the real destination and the command
    // re-defaults it to an implicit `me` the strict scorers rightly ignore.
    if (norm === 'me' || norm === 'it' || norm === 'you') return false;
    return true;
  }

  /**
   * Fold a depth-balanced `{ … }` token run into ONE literal value (see the
   * call site in matchRoleToken). The tokenizers emit `{`, `}`, `:`, `;`, `$`
   * as bare identifier tokens, so a CSS style-object argument is otherwise
   * unmatchable by a single-token role. Depth tracking covers the nested
   * `${expr}` template braces. Returns null (with the stream reset) when the
   * run never closes or exceeds the token cap.
   */
  private static readonly MAX_BRACE_RUN_TOKENS = 64;

  private tryMatchBraceRunLiteral(tokens: TokenStream): SemanticValue | null {
    const open = tokens.peek();
    if (!open || open.kind !== 'identifier' || open.value !== '{') return null;

    const mark = tokens.mark();
    tokens.advance(); // {
    const parts: string[] = ['{'];
    let depth = 1;
    let guard = 0;
    while (!tokens.isAtEnd() && guard++ < PatternMatcher.MAX_BRACE_RUN_TOKENS) {
      const t = tokens.advance();
      if (!t) break;
      if (t.value === '{') depth++;
      else if (t.value === '}') {
        depth--;
        if (depth === 0) {
          parts.push('}');
          return createLiteral(parts.join(' '));
        }
      }
      parts.push(t.value);
    }

    tokens.reset(mark);
    return null;
  }

  /**
   * Fold a depth-balanced `{ … }` token run into ONE **expression** value whose
   * `raw` is the original source text — the object-literal sibling of
   * tryMatchBraceRunLiteral, for roles that expect an expression (`fetch`'s
   * request-options `style` role: `fetch /x with { method: 'POST' }`).
   *
   * Unlike the literal fold, this cannot space-join token values: `raw` is handed
   * to the expression parser, and a template-literal option
   * (``{ headers: {Authorization: `Bearer ${token}`} }``) shatters into many
   * tokens whose rejoined spacing must match the source or the interpolation
   * breaks. Tokens carry source offsets, so inter-token gaps are reproduced
   * exactly. Returns null (stream reset) when the run never closes.
   */
  private tryMatchBraceRunExpression(tokens: TokenStream): SemanticValue | null {
    const open = tokens.peek();
    if (!open || open.kind !== 'identifier' || open.value !== '{') return null;

    const mark = tokens.mark();
    let raw = '';
    let prevEnd = -1;
    let depth = 0;
    let guard = 0;
    while (!tokens.isAtEnd() && guard++ < PatternMatcher.MAX_BRACE_RUN_TOKENS) {
      const t = tokens.advance();
      if (!t) break;
      if (prevEnd >= 0 && t.position.start > prevEnd) {
        raw += ' '.repeat(t.position.start - prevEnd);
      }
      raw += t.value;
      prevEnd = t.position.end;

      if (t.value === '{') depth++;
      else if (t.value === '}') {
        depth--;
        if (depth === 0) return { type: 'expression', raw } as SemanticValue;
      }
    }

    tokens.reset(mark);
    return null;
  }

  /**
   * Try to match a bare function-call expression like `myFunction()` or
   * `adjustLayout(a, b)`. The multilingual tokenizers split this into
   * `name` `(` [args] `)` with the parens as `identifier`-kind tokens (not
   * `punctuation`, which `tryMatchMethodCallExpression` requires). Folds the
   * whole call into one `expression` value so an SOV role (`{patient} <marker>`)
   * captures `name(...)` instead of just `name`, leaving the marker to match.
   * Returns null (no stream movement) unless a balanced `name( … )` is present.
   */
  private tryMatchBareCallExpression(tokens: TokenStream): SemanticValue | null {
    const token = tokens.peek();
    if (!token || token.kind !== 'identifier') return null;
    // Must look like a function name (rejects selectors/urls/`:local`/`$global`).
    if (!/^[A-Za-z_][\w$]*$/.test(token.value)) return null;
    const next = tokens.peek(1);
    if (!next || next.value !== '(') return null;

    const mark = tokens.mark();
    tokens.advance(); // function name
    const openParen = tokens.advance(); // (

    // Reconstruct the parenthesized content VERBATIM by source adjacency rather
    // than collecting tokens and re-joining with `, `. The tokenizer splits the
    // call into separate tokens (`value` `:` `42`), and the old comma-rejoin
    // assumed a positional arg list — so a named-argument / detail call
    // `update(value: 42)` came out as `update(value, :, 42)` (the `:` became a
    // phantom arg). Adjacency-joining preserves every shape: `a, b` stays `a, b`,
    // `value: 42` stays `value: 42` (a token glues to the previous when it abuts
    // it in the source, else a single space separates them).
    let content = '';
    let prevEnd = openParen.position?.end;
    let closed = false;
    let guard = 0;
    while (!tokens.isAtEnd() && guard++ < PatternMatcher.MAX_METHOD_ARGS + 2) {
      const argToken = tokens.peek();
      if (!argToken) break;
      if (argToken.value === ')') {
        tokens.advance(); // consume )
        closed = true;
        break;
      }
      const adjacent = prevEnd !== undefined && argToken.position?.start === prevEnd;
      content += (content === '' || adjacent ? '' : ' ') + argToken.value;
      prevEnd = argToken.position?.end;
      tokens.advance();
    }

    if (!closed) {
      tokens.reset(mark);
      return null;
    }

    return { type: 'expression', raw: `${token.value}(${content})` } as SemanticValue;
  }

  /**
   * Try to match a method call expression like '#dialog.showModal()'.
   * Pattern: selector + '.' + identifier + '(' + [args] + ')'
   * Returns an expression value if matched, or null if not.
   */
  private tryMatchMethodCallExpression(tokens: TokenStream): SemanticValue | null {
    const token = tokens.peek();
    if (!token || token.kind !== 'selector') return null;

    // Look ahead for: . identifier (
    const mark = tokens.mark();
    tokens.advance(); // consume selector

    const dotToken = tokens.peek();
    if (!dotToken || dotToken.kind !== 'operator' || dotToken.value !== '.') {
      tokens.reset(mark);
      return null;
    }
    tokens.advance(); // consume .

    const methodToken = tokens.peek();
    if (!methodToken || methodToken.kind !== 'identifier') {
      tokens.reset(mark);
      return null;
    }
    tokens.advance(); // consume method name

    const openParen = tokens.peek();
    if (!openParen || openParen.kind !== 'punctuation' || openParen.value !== '(') {
      tokens.reset(mark);
      return null;
    }
    tokens.advance(); // consume (

    // Consume arguments until we find ) (with depth limit for security)
    const args: string[] = [];
    while (!tokens.isAtEnd() && args.length < PatternMatcher.MAX_METHOD_ARGS) {
      const argToken = tokens.peek();
      if (!argToken) break;
      if (argToken.kind === 'punctuation' && argToken.value === ')') {
        tokens.advance(); // consume )
        break;
      }
      // Skip commas
      if (argToken.kind === 'punctuation' && argToken.value === ',') {
        tokens.advance();
        continue;
      }
      // Collect arg value
      args.push(argToken.value);
      tokens.advance();
    }

    // Create expression value: #dialog.showModal()
    const methodCall = `${token.value}.${methodToken.value}(${args.join(', ')})`;
    return {
      type: 'expression',
      raw: methodCall,
    } as SemanticValue;
  }

  /**
   * `( <expr> ) .method ( <args> )` → expression `(<expr>).method(<args>)`.
   * The receiver's keywords contribute their English form (`(más_cercano
   * <form/>)` → `(closest <form/>)`), as every raw expression does, so a
   * localized receiver re-parses to the same English. Null, consuming
   * nothing, unless the whole shape is present.
   */
  private tryMatchParenReceiverCall(tokens: TokenStream): SemanticValue | null {
    if (tokens.peek()?.value !== '(') return null;
    const mark = tokens.mark();
    const inner: LanguageToken[] = [];
    tokens.advance();
    let depth = 1;
    while (!tokens.isAtEnd() && inner.length <= PatternMatcher.MAX_METHOD_ARGS * 4) {
      const t = tokens.peek()!;
      tokens.advance();
      if (t.value === '(') depth++;
      else if (t.value === ')' && --depth === 0) break;
      inner.push(t);
    }
    const closer = tokens.tokens[tokens.position() - 1];
    const method = tokens.peek();
    if (
      depth !== 0 ||
      inner.length === 0 ||
      !method ||
      method.kind !== 'selector' ||
      !/^\.[A-Za-z_$][\w$]*$/.test(method.value) ||
      !PatternMatcher.abuts(closer, method)
    ) {
      tokens.reset(mark);
      return null;
    }
    tokens.advance();
    const argsOpen = tokens.peek();
    if (argsOpen?.value !== '(' || !PatternMatcher.abuts(method, argsOpen)) {
      tokens.reset(mark);
      return null;
    }
    const args = this.consumeCallParens(tokens);
    if (args === null) {
      tokens.reset(mark);
      return null;
    }
    const receiver = joinExpressionTokens(inner, this.currentProfile);
    return { type: 'expression', raw: `(${receiver})${method.value}${args}` } as SemanticValue;
  }

  /**
   * A parenthesized group that STANDS ALONE as a role value: the
   * `(closest <form/>)` of `morph (closest <form/>) to it`, `hide (closest .modal)`,
   * `put it into (closest <form/>)`. Parentheses are how hyperscript passes a
   * computed target — upstream reads the unparenthesized `morph closest <form/> to
   * it` as `morph (closest <form/> to it)`, since `closest` owns a `to` clause — yet
   * no step captured a bare group: the single-token capture took the lone `(`
   * and stranded the rest, or a `selector|reference` slot rejected it and the
   * whole command dropped. The English reference lost it, so every translation
   * did too, with every multilingual signal green (morph-form-update, #1167).
   *
   * The interior joins through `joinExpressionTokens`, so it is normalized to
   * English here and localized again by the renderer — the operator run's seam.
   * Declines, consuming nothing, when the group does not stand alone: followed
   * by a possessive `'s`, a binary operator, or an abutting `.member` / `(call)`
   * — the possessive matchers, the operator run and the paren receiver call own
   * those shapes. An unbalanced or empty group declines too.
   */
  private tryMatchParenGroupExpression(tokens: TokenStream): SemanticValue | null {
    if (tokens.peek()?.value !== '(') return null;
    const mark = tokens.mark();
    tokens.advance();
    const inner: LanguageToken[] = [];
    let depth = 1;
    while (!tokens.isAtEnd()) {
      const t = tokens.peek()!;
      tokens.advance();
      if (t.value === '(') depth++;
      else if (t.value === ')' && --depth === 0) break;
      inner.push(t);
    }
    const closer = tokens.tokens[tokens.position() - 1];
    const next = tokens.peek();
    const continues =
      !!next &&
      (next.value === "'" ||
        RUN_OPERATORS.has(next.value) ||
        (PatternMatcher.abuts(closer, next) && (next.value.startsWith('.') || next.value === '(')));
    if (depth !== 0 || inner.length === 0 || continues) {
      tokens.reset(mark);
      return null;
    }
    const raw = `(${joinExpressionTokens(inner, this.currentProfile)})`;
    return { type: 'expression', raw, value: raw } as SemanticValue;
  }

  /** Whether `b` directly follows `a` in the source, with no whitespace between. */
  private static abuts(a: LanguageToken | undefined, b: LanguageToken | undefined): boolean {
    return !!a && !!b && a.position.end === b.position.start;
  }

  /**
   * Consume a balanced `(…)` run at the stream position and return its text
   * joined without spaces (`(1,2)`), or null — consuming nothing — when the
   * parens do not balance. The paren tokens are plain identifiers in the
   * multilingual tokenizers, so this balances by VALUE, mirroring
   * tryConsumeRunOperand's group logic.
   */
  private consumeCallParens(tokens: TokenStream): string | null {
    const callMark = tokens.mark();
    const callParts: string[] = [];
    let parenDepth = 0;
    while (!tokens.isAtEnd()) {
      const t = tokens.peek();
      if (!t) break;
      callParts.push(t.value);
      tokens.advance();
      if (t.value === '(') parenDepth++;
      else if (t.value === ')' && --parenDepth === 0) return callParts.join('');
    }
    tokens.reset(callMark); // unbalanced — leave the parens unconsumed
    return null;
  }

  /**
   * Try to match a property access expression like 'userData.name' or 'it.data'.
   * Pattern: (identifier | keyword) + '.' + identifier [+ '.' + identifier ...]
   * Returns an expression value if matched, or null if not.
   */
  private tryMatchPropertyAccessExpression(
    tokens: TokenStream,
    allowPropertyPath = true
  ): SemanticValue | null {
    const token = tokens.peek();
    if (!token) return null;

    // Must start with an identifier or keyword reference
    if (token.kind !== 'identifier' && token.kind !== 'keyword') return null;

    // Look ahead for: . identifier
    const mark = tokens.mark();
    tokens.advance(); // consume first token

    // Fused-dot form: the tokenizer emits `it.value` / `event.detail.message` as
    // a base token + `.`-prefixed *selector* tokens (`.value`, `.detail`), since
    // `.foo` looks like a class selector. Fold a chain of those into the property
    // path (the `.` operator path below handles the rare un-fused form).
    //
    // Gated to identifiers and known reference bases so a command verb followed
    // by a class selector (`بدل .active` = "toggle .active") is never swallowed as
    // a property access — command verbs are never reference bases.
    const baseLower = token.value.toLowerCase();
    const fusedFirst = tokens.peek();
    // Only a `.prop` that TOUCHES its base is a property: `:x .item`, spaced,
    // is a variable and a class (`set :x to .item`, which it/pl/ru/uk render
    // with the value unmarked after the variable).
    if (
      (token.kind === 'identifier' || PatternMatcher.PROPERTY_ACCESS_BASES.has(baseLower)) &&
      fusedFirst &&
      fusedFirst.kind === 'selector' &&
      /^\.[a-zA-Z_]/.test(fusedFirst.value) &&
      fusedFirst.position.start === token.position.end
    ) {
      let fusedChain = token.value;
      const fusedProps: string[] = [];
      let fusedDepth = 0;
      while (fusedDepth < PatternMatcher.MAX_PROPERTY_DEPTH) {
        const prop = tokens.peek();
        if (prop && prop.kind === 'selector' && /^\.[a-zA-Z_]/.test(prop.value)) {
          fusedChain += `.${prop.value.slice(1)}`;
          fusedProps.push(prop.value.slice(1));
          tokens.advance();
          fusedDepth++;
        } else {
          break;
        }
      }
      // A fused `<ref>.<prop>` access where the base is a real reference
      // (`it.error`, `result.name`, `event.ctrlKey`) is semantically a property
      // access — emit property-path so it aligns with the translations that
      // already render the possessive as property-path (the put/set.patient R1
      // arc). Guard against a trailing method call (`target.closest("li")`): the
      // fused path consumes the `.`-selector props but a following `(` means it's
      // a method call, which must stay an expression (behavior-sortable).
      const fusedTrailing = tokens.peek();
      const fusedIsMethodCall = !!fusedTrailing && fusedTrailing.value.startsWith('(');
      if (
        allowPropertyPath &&
        !fusedIsMethodCall &&
        fusedProps.length > 0 &&
        isValidReference(baseLower)
      ) {
        return createPropertyPath(createReference(baseLower), fusedProps.join('.'), 'dot');
      }
      if (fusedIsMethodCall) {
        // Consume the balanced call parens into the expression. Left in the
        // stream they broke the surrounding SOV pattern — the marker expected
        // after the role slot (`… に 設定`) met `(` instead, the whole set
        // pattern died, and the command fell to the role-swapping
        // verb-anchoring fallback (behavior-sortable `set item to the
        // target.closest("li")`, session-5 residue).
        fusedChain += this.consumeCallParens(tokens) ?? '';
      }
      return { type: 'expression', raw: fusedChain } as SemanticValue;
    }

    const dotToken = tokens.peek();
    if (!dotToken || dotToken.kind !== 'operator' || dotToken.value !== '.') {
      tokens.reset(mark);
      return null;
    }
    tokens.advance(); // consume .

    const propertyToken = tokens.peek();
    if (!propertyToken || propertyToken.kind !== 'identifier') {
      tokens.reset(mark);
      return null;
    }
    tokens.advance(); // consume property name

    // Build the property chain
    let chain = `${token.value}.${propertyToken.value}`;
    let depth = 1; // Already have one property access

    // Continue for nested property access (e.g., userData.address.city)
    // With depth limit for security
    while (!tokens.isAtEnd() && depth < PatternMatcher.MAX_PROPERTY_DEPTH) {
      const nextDot = tokens.peek();
      if (!nextDot || nextDot.kind !== 'operator' || nextDot.value !== '.') {
        break;
      }
      tokens.advance(); // consume .

      const nextProp = tokens.peek();
      if (!nextProp || nextProp.kind !== 'identifier') {
        // Dot without property - put the dot back and stop
        // Can't easily put a single token back, so we'll include it
        break;
      }
      tokens.advance(); // consume property
      chain += `.${nextProp.value}`;
      depth++;
    }

    // Check for method call: chain + '(' + args + ')'
    // e.g., me.insertBefore(draggedItem, dropTarget)
    const openParen = tokens.peek();
    if (openParen && openParen.kind === 'punctuation' && openParen.value === '(') {
      tokens.advance(); // consume (

      // Collect arguments (comma-separated values)
      const args: string[] = [];
      let argDepth = 0; // Track nested parentheses
      while (!tokens.isAtEnd() && args.length < PatternMatcher.MAX_METHOD_ARGS) {
        const argToken = tokens.peek();
        if (!argToken) break;

        // Handle close paren - respecting nesting
        if (argToken.kind === 'punctuation' && argToken.value === ')') {
          if (argDepth === 0) {
            tokens.advance(); // consume )
            break;
          }
          argDepth--;
        }
        // Track nested open parens
        if (argToken.kind === 'punctuation' && argToken.value === '(') {
          argDepth++;
        }
        // Skip commas between arguments
        if (argToken.kind === 'punctuation' && argToken.value === ',') {
          tokens.advance();
          continue;
        }
        // Collect arg value
        args.push(argToken.value);
        tokens.advance();
      }

      // Create expression value with method call: me.insertBefore(a, b)
      const methodCall = `${chain}(${args.join(', ')})`;
      return {
        type: 'expression',
        raw: methodCall,
      } as SemanticValue;
    }

    // Un-fused `<ref> . <prop>` where the base is a real reference →
    // property-path (mirrors the fused-dot path above). Plain identifiers
    // (`userData.name`) keep the expression form. The method-call shape is
    // already handled above (the `(` branch returns an expression before here).
    const opBaseLower = token.value.toLowerCase();
    if (allowPropertyPath && isValidReference(opBaseLower)) {
      const opProps = chain.split('.').slice(1).join('.');
      return createPropertyPath(createReference(opBaseLower), opProps, 'dot');
    }
    // Create expression value: userData.name
    return {
      type: 'expression',
      raw: chain,
    } as SemanticValue;
  }

  /**
   * Try to match a possessive selector expression like "#element's *opacity".
   * Pattern: selector + possessive-marker + (selector | identifier)
   *
   * The possessive marker is English `'s` (a punctuation token) or, for other
   * languages, the active profile's `possessive.marker` emitted as a `particle`
   * token (e.g. Japanese `の`, Korean `의`). Returns a property-path value if
   * matched, or null if not.
   */
  private tryMatchPossessiveSelectorExpression(tokens: TokenStream): SemanticValue | null {
    const token = tokens.peek();
    if (!token || token.kind !== 'selector') return null;

    // Look ahead for the possessive marker.
    const mark = tokens.mark();
    tokens.advance(); // consume selector

    const possessiveToken = tokens.peek();
    // Agglutinative suffix markers may be written with a leading hyphen in the
    // profile (e.g. Quechua `-pa`) but tokenize without it; normalize before
    // comparing.
    const profileMarker = this.currentProfile?.possessive?.marker?.replace(/^-/, '');
    const isEnglishPossessive =
      !!possessiveToken && possessiveToken.kind === 'punctuation' && possessiveToken.value === "'s";
    // The en tokenizer splits the possessive clitic `'s` into two tokens (`'` + `s`)
    // when it trails a selector — `#picker's value` → `#picker ' s value` — so the
    // single-token check above misses it and the property (`value`/`textContent`) is
    // dropped, capturing only the bare `#picker` selector. ja/ko/… keep their の/의
    // possessive whole, so they captured the full property-path while en lost it (the
    // bind-explicit-property / bind-non-form-display R1 cap). Detect the split pair.
    const splitEnglishPossessive =
      !!possessiveToken &&
      possessiveToken.value === "'" &&
      (tokens.peek(1)?.value ?? '').toLowerCase() === 's';
    // Non-English markers (の, 의, র, pa, …) arrive as `particle` tokens. Match
    // them against the active profile's possessive marker. Empty markers
    // (Turkish, suffix-fused) don't have a standalone token and aren't handled here.
    //
    // Marker-role collision gate: a Romance genitive connector doubles as a
    // role marker (es/pt `de` is BOTH the possessive connector AND remove's
    // from-marker, normalized `source`). When the marker normalizes to `source`
    // and the CURRENT command's schema declares a source role, the marker
    // belongs to the command — folding it as a possessive steals the pattern's
    // own marker (`quitar .{dragClass} de item` read as ".{dragClass}'s item" →
    // phantom property-path patient, source lost to the me-default). `set`
    // declares no `source`, so set-color-variable's genitive fold is untouched.
    // Deliberately `source`-ONLY: the SOV genitives qu `pa` / tr `nin`
    // normalize to `destination`, and their possessive folds are load-bearing
    // on commands that declare a destination (qu/tr bind-explicit-property /
    // bind-non-form-display parse NULL when gated on it).
    const markerRoleCollision = (() => {
      if (!this.currentPatternCommand) return false;
      if ((possessiveToken?.normalized ?? '').toLowerCase() !== 'source') return false;
      const schema = (commandSchemas as Record<string, CommandSchema | undefined>)[
        this.currentPatternCommand
      ];
      return !!schema && schema.roles.some(r => r.role === 'source');
    })();
    const isProfilePossessive =
      !!possessiveToken &&
      !!profileMarker &&
      profileMarker !== "'s" &&
      possessiveToken.value === profileMarker &&
      // `identifier` belongs here alongside the clitic kinds: a language whose
      // possessive marker is a FREE WORD (tl `ng`, vi `của`) tokenizes it as a
      // plain identifier, not a particle, so the exact-value check above is the
      // only thing that identifies it — and it is exact, against this profile's
      // own declared marker.
      (possessiveToken.kind === 'particle' ||
        possessiveToken.kind === 'punctuation' ||
        possessiveToken.kind === 'identifier') &&
      !markerRoleCollision;
    if (!isEnglishPossessive && !isProfilePossessive && !splitEnglishPossessive) {
      tokens.reset(mark);
      return null;
    }
    tokens.advance(); // consume the possessive marker (`'s`, profile marker, or split `'`)
    if (splitEnglishPossessive) tokens.advance(); // consume the trailing `s` of the split pair

    const propertyToken = tokens.peek();
    if (!propertyToken) {
      tokens.reset(mark);
      return null;
    }

    // English keeps the historical selector-or-identifier property (e.g.
    // `#element's *opacity`). For profile markers, only an identifier is a
    // property — otherwise a target+patient construct like `#button の .active`
    // ("toggle .active on #button") would be mis-read as a property path.
    // A `keyword` property is also valid for the English `'s` (whole or split):
    // some property names translate to a profile KEYWORD rather than a bare
    // identifier (vi `value` → `giá trị`, a single keyword token), so without this
    // the English-possessive `#picker's giá trị` lost its property and fell back to a
    // bare `#picker` selector — mismatching the en reference's property-path.
    // A `*`-sigil token is hyperscript's STYLE property and can only ever be a
    // property, so it is safe on a profile marker too — and it is exactly what
    // the danger case above is not. Without it `#themeの*background-color`
    // (ja/ko/zh/bn) and `#theme ng *background-color` (tl/vi) fell through to
    // the of-possessive matcher, which reads `X <marker> Y` as "X of Y" and so
    // folded the pair INVERTED — `set *background-color's #theme`, at every
    // fidelity score 1.0 (set-color-variable, six kept rows).
    const propertyIsStyleSigil =
      propertyToken.kind === 'selector' && propertyToken.value.startsWith('*');
    // A property name that translates to a profile KEYWORD rather than a bare
    // identifier (vi `value` → `giá trị`, one keyword token) was accepted for
    // the English `'s` and refused for a profile marker, so vi's own possessive
    // (`#picker của giá trị`) matched neither this matcher nor the of-matcher —
    // whose owner slot then held a keyword, not a selector — and `bind.source`
    // was lost outright (vi bind-explicit-property). The language's property
    // table is the voucher: a keyword is admitted only when the table names it
    // a property, so a command verb after the marker (`#button の 切り替え`) is
    // still refused.
    const propertyIsNamedProperty =
      propertyToken.kind === 'keyword' &&
      !!this.currentProfile &&
      isKnownPropertySurface(this.currentProfile.code, propertyToken.value);
    const propertyOk =
      propertyToken.kind === 'identifier' ||
      propertyIsStyleSigil ||
      propertyIsNamedProperty ||
      ((isEnglishPossessive || splitEnglishPossessive) &&
        (propertyToken.kind === 'selector' || propertyToken.kind === 'keyword'));
    if (!propertyOk) {
      tokens.reset(mark);
      return null;
    }
    tokens.advance(); // consume property

    // Create property-path: #element's *opacity (property normalized to English:
    // `#picker's 値`/`giá trị` → `#picker's value`).
    return createPropertyPath(
      createSelector(token.value),
      this.toEnglishProperty(propertyToken.value),
      'possessive'
    );
  }

  /**
   * Try to match a selector + property expression like "#output.innerText".
   * This handles cases where the tokenizer produces two selector tokens:
   * - #output (id selector)
   * - .innerText (looks like class selector, but is actually property)
   *
   * Pattern: id-selector + class-selector-that-is-actually-property
   * Returns a property-path value if matched, or null if not.
   */
  private tryMatchSelectorPropertyExpression(tokens: TokenStream): SemanticValue | null {
    const token = tokens.peek();
    if (!token || token.kind !== 'selector') return null;

    // Must be an ID selector (starts with #)
    if (!token.value.startsWith('#')) return null;

    // Look ahead for: selector that looks like a property (.something)
    const mark = tokens.mark();
    tokens.advance(); // consume first selector

    const propertyToken = tokens.peek();
    if (!propertyToken || propertyToken.kind !== 'selector') {
      tokens.reset(mark);
      return null;
    }

    // Second token must look like a class selector (starts with .)
    // but we interpret it as a property access
    if (!propertyToken.value.startsWith('.')) {
      tokens.reset(mark);
      return null;
    }

    tokens.advance(); // consume property selector

    // The chain continues through each further `.prop` (`#d1.value.length`).
    // Only the first was read, and the rest stranded:
    // `put #d1.value.length into #out` lost the whole put, and `set x to
    // #d1.value.length` set `#d1.value`.
    const chain = [propertyToken.value.slice(1)];
    let last = propertyToken;
    for (
      let next = tokens.peek();
      next &&
      next.kind === 'selector' &&
      /^\.[a-zA-Z_]/.test(next.value) &&
      chain.length < PatternMatcher.MAX_PROPERTY_DEPTH;
      next = tokens.peek()
    ) {
      chain.push(next.value.slice(1));
      last = next;
      tokens.advance();
    }

    // A method CALL on the element (`call #dialog.showModal()`, `call
    // #x.foo(1, 2)`) is an expression, not a property path: folding it to
    // `#x.foo` stranded the argument list and turned the call into a property
    // read — the render came back `call #x.foo`, which calls nothing. Only a
    // GLUED `(` is an argument list: a spaced one is the next operand
    // (it/pl/ru/uk render `set` as `impostare in #total.innerText ( … ) * …`).
    const openParen = tokens.peek();
    if (openParen?.value === '(' && PatternMatcher.abuts(last, openParen)) {
      const args = this.consumeCallParens(tokens);
      if (args !== null) {
        return {
          type: 'expression',
          raw: `${token.value}.${chain.join('.')}${args}`,
        } as SemanticValue;
      }
    }

    // Create property-path: #output.innerText, #d1.value.length
    return createPropertyPath(createSelector(token.value), chain.join('.'), 'dot');
  }

  /**
   * Match a group pattern token (optional sequence).
   */
  private matchGroupToken(
    tokens: TokenStream,
    patternToken: PatternToken & { type: 'group' },
    captured: Map<SemanticRole, SemanticValue>,
    nextPatternToken?: PatternToken
  ): boolean {
    const mark = tokens.mark();

    // Snapshot the captured roles' VALUES, not just their keys. Clearing only
    // the newly-added keys leaves an OVERWRITE by a failed group in place.
    //
    // 90 shipped patterns bind one role in TWO optional groups — every
    // `<cmd>-event-<lang>-sov` has a pre-verb `[{destination} <marker>]` and a
    // post-verb one, because the transformer emits the to-phrase on either side
    // depending on the command. When the trailing group speculatively captures
    // the next token into that role and then fails on its marker, the token
    // stream rolls back but the corrupted value survives: ja
    // `クリック で #panel に .active を トグル ゴミ` returned
    // `destination="ゴミ"` with the real `#panel` captured and discarded.
    const capturedBefore = new Map(captured);

    let success = this.matchTokenSequence(tokens, patternToken.tokens, captured, nextPatternToken);

    // A postpositional marker group ([{destination} [e]]) can "succeed" by
    // capturing ONLY the role: the role is optional-in-group, the trailing
    // marker sub-group is optional too, so `.loading i 2s değiştir` binds
    // `.loading` as the DESTINATION and the pattern then dies on `patient` —
    // with no backtracking, the whole (correct) parse is lost. A role captured
    // without its own marker is indistinguishable from the next slot's value,
    // so require the marker: if this group added a role capture but none of
    // the tokens it consumed matched a `renderRequired` marker sub-group's
    // literal, fail the group and roll back — the value stays available for
    // the slot that really owns it. Scoped to groups that carry a
    // renderRequired literal-only sub-group (the generated postpositional
    // shape); prepositional groups ([จาก {source}]) fail fast on their leading
    // literal and never reach this.
    if (success) {
      const markerLiterals: string[] = [];
      for (const inner of patternToken.tokens) {
        if (inner.type === 'group' && (inner as { renderRequired?: boolean }).renderRequired) {
          for (const t of inner.tokens) {
            if (t.type === 'literal') markerLiterals.push(t.value, ...(t.alternatives ?? []));
          }
        }
      }
      if (markerLiterals.length > 0) {
        const addedRole = [...captured.keys()].some(role => !capturedBefore.has(role));
        if (addedRole) {
          const consumed = tokens.tokens.slice(mark.position, tokens.position()) as LanguageToken[];
          const markerSeen = consumed.some(tok =>
            markerLiterals.some(lit => this.getMatchType(tok, lit) !== 'none')
          );
          if (!markerSeen) success = false;
        }
      }
    }

    if (!success) {
      tokens.reset(mark);
      // Restore the pre-group capture state exactly: drop roles the failed group
      // added, and put back the values it overwrote.
      for (const role of captured.keys()) {
        if (!capturedBefore.has(role)) {
          captured.delete(role);
        }
      }
      for (const [role, value] of capturedBefore) {
        if (captured.get(role) !== value) {
          captured.set(role, value);
        }
      }
      return patternToken.optional || false;
    }

    return true;
  }

  /**
   * Get the type of match for a token against a value.
   * Used for confidence calculation.
   */
  private getMatchType(
    token: LanguageToken,
    value: string
  ): 'exact' | 'normalized' | 'stem' | 'case-insensitive' | 'none' {
    // Exact match (highest confidence)
    if (token.value === value) return 'exact';

    // Explicit keyword map normalized match (high confidence)
    if (token.normalized === value) return 'normalized';

    // Morphologically normalized stem match (medium-high confidence)
    // Only accept if stem confidence is reasonable
    if (token.stem === value && token.stemConfidence !== undefined && token.stemConfidence >= 0.7) {
      return 'stem';
    }

    // Case-insensitive match for keywords (medium confidence)
    if (token.kind === 'keyword' && token.value.toLowerCase() === value.toLowerCase()) {
      return 'case-insensitive';
    }

    // Diacritic-insensitive match for keywords (medium confidence). Arabic
    // harakat are OPTIONAL in the script — `بدّل` and `بَدِّل` are the same word,
    // and a pattern literal carries whichever spelling the profile declared. The
    // keyword map is queried diacritic-insensitively, so the token is correctly
    // recognized; without this the surface still fails to match its own literal
    // here, one layer later. Restricted to keywords, and `stripOptionalDiacritics`
    // touches only Arabic-exclusive codepoints, so it is inert for every other
    // script (Latin accents are meaning-bearing and must never be stripped).
    if (token.kind === 'keyword') {
      const strippedToken = stripOptionalDiacritics(token.value).toLowerCase();
      if (strippedToken && strippedToken === stripOptionalDiacritics(value).toLowerCase()) {
        return 'case-insensitive';
      }
    }

    return 'none';
  }

  /**
   * Track stem matches for confidence calculation.
   * This is set during matching and read during confidence calculation.
   */
  private stemMatchCount: number = 0;
  private totalKeywordMatches: number = 0;

  // ==========================================================================
  // Depth Limits for Expression Parsing (security hardening)
  // ==========================================================================

  /** Maximum depth for nested property access (e.g., a.b.c.d...) */
  private static readonly MAX_PROPERTY_DEPTH = 10;

  /** Maximum number of arguments in method calls */
  private static readonly MAX_METHOD_ARGS = 20;

  /**
   * Commands whose primary argument is a DECLARATION signature `name(params)`, not
   * a call expression — the bare-call fold must not consume the `(params)` for
   * these (see the fold call site in matchRoleToken).
   */
  private static readonly DECLARATION_COMMANDS = new Set(['behavior', 'def', 'install']);

  /** The role each command writes: a counter's patient, `set`'s destination. */
  private static readonly WRITTEN_ROLE: Readonly<Record<string, string>> = {
    increment: 'patient',
    decrement: 'patient',
    set: 'destination',
  };

  /**
   * The value a command writes. It is never an operator run or a call:
   * neither can be written. Where a value follows it with no marker
   * (`tambah_satu i - 2`, `增加 把 i (n + 1)`, pl `ustaw do x (n + 1)`),
   * reading one took the value into the target, which upstream then rejects
   * as unwritable.
   */
  private isWrittenTarget(patternToken: PatternToken & { type: 'role' }): boolean {
    return PatternMatcher.WRITTEN_ROLE[this.currentRoleCommand ?? ''] === patternToken.role;
  }

  /** A lone token's value (token-value.ts). */
  private tokenToSemanticValue(token: LanguageToken): SemanticValue | null {
    return tokenValue(token);
  }

  /**
   * Apply extraction rules to fill in static values and defaults for missing
   * roles, and re-shape captured roles that declare a `transform` (the
   * ExtractionRule field was documented but unwired — go-qu-url-dest is the
   * first consumer: it re-types the marker-bound capture to the expression
   * shape the en reference produces).
   */
  private applyExtractionRules(
    pattern: LanguagePattern,
    captured: Map<SemanticRole, SemanticValue>
  ): void {
    for (const [role, rule] of Object.entries(pattern.extraction)) {
      const existing = captured.get(role as SemanticRole);
      if (existing) {
        if (rule.transform) {
          const raw =
            existing.type === 'literal'
              ? String(existing.value)
              : String((existing as { raw?: unknown }).raw ?? '');
          captured.set(role as SemanticRole, rule.transform(raw));
        }
      } else if (rule.value !== undefined) {
        // Static value extraction (e.g., action: { value: "toggle" })
        captured.set(role as SemanticRole, { type: 'literal', value: rule.value });
      } else if (rule.default) {
        // Copied, never aliased (the rule's default object is shared across
        // parses), and tagged implicit: materialized from the default, not
        // captured from source text — renderers suppress implicit values only.
        captured.set(role as SemanticRole, { ...rule.default, implicit: true });
      }
    }
  }

  /**
   * Check if a pattern token is optional.
   */
  private isOptional(patternToken: PatternToken): boolean {
    return patternToken.type !== 'literal' && patternToken.optional === true;
  }

  // Confidence scoring delegated to ConfidenceModel (Phase 3.3)
  // See confidence-model.ts for DefaultConfidenceModel implementation

  // ===========================================================================
  // English Idiom Support - Noise Word Handling
  // ===========================================================================

  /**
   * Noise words that can be skipped in English for more natural syntax.
   * - "the" before selectors: "toggle the .active" → "toggle .active"
   * - "the" before identifiers: "set the color to red" → "set color to red"
   * - "class" after class selectors: "add the .visible class" → "add .visible"
   */
  private static readonly ENGLISH_NOISE_WORDS = new Set(['the', 'a', 'an']);

  /**
   * Skip noise words like "the" before selectors and identifiers.
   * This enables more natural English syntax like "toggle the .active"
   * and "set the color to red".
   */
  private skipNoiseWords(
    tokens: TokenStream,
    patternToken: PatternToken & { type: 'role' },
    nextPatternToken?: PatternToken
  ): void {
    const token = tokens.peek();
    if (!token) return;

    const tokenLower = token.value.toLowerCase();

    // Check if current token is a noise word (like "the")
    if (PatternMatcher.ENGLISH_NOISE_WORDS.has(tokenLower)) {
      // `a`/`an` before an operator or the next marker is a variable (C1, C2).
      if (articleIsVariable(this.slotAt(tokens, patternToken, nextPatternToken))) return;
      // Look ahead to see if the next token is a selector or identifier
      const mark = tokens.mark();
      tokens.advance();
      const nextToken = tokens.peek();

      if (nextToken && (nextToken.kind === 'selector' || nextToken.kind === 'identifier')) {
        // Keep the position after "the" - effectively skipping it
        return;
      }

      // `the <ref>.<prop>` where the reference base tokenizes as a KEYWORD and
      // the property as a fused `.`-selector (`the target .closest ("li")` in
      // the non-English tokenizers — en fuses `target.closest` into one
      // identifier and takes the branch above). Without the skip the role slot
      // captures the bare article and the pattern dies on the following
      // keyword (behavior-sortable's `set item to the target.closest("li")`).
      // Gated on the property-access SHAPE (keyword + `.`-selector), so `the
      // <event-noun> <marker>` keeps the leaked-article handling below.
      const afterNext = tokens.peek(1);
      if (
        nextToken &&
        nextToken.kind === 'keyword' &&
        afterNext &&
        afterNext.kind === 'selector' &&
        /^\.[a-zA-Z_]/.test(afterNext.value)
      ) {
        return;
      }

      // "the next <sel>" / "the closest .modal": the article also precedes
      // positional-phrase heads, which tokenize as keywords/literals — skip it
      // so tryMatchPositionalExpression sees the positional keyword first.
      const nextNorm = nextToken ? (nextToken.normalized ?? nextToken.value).toLowerCase() : '';
      if (nextToken && PatternMatcher.POSITIONAL_OR_SCOPE_KEYWORDS.has(nextNorm)) {
        return;
      }

      // Leaked English article before a FRONTED reference-noun patient: in a
      // non-English parse `the` is never authored — it's a dict/transformer leak
      // (hi `the घटना को …` = "the event", the fronted halt patient). Without
      // skipping it the patient role grabs only `the` and the following marker
      // fails, so `halt-event-hi-sov-patient-first` (`{patient} को {event} पर
      // रोकें`) never matches and the halt loses its patient (a bare halt stops
      // the handler). The reference noun (घटना→event) then fills the patient, so
      // `halt the event` continues.
      //
      // Fires for a fronted/leaked-article reference-noun patient when the noun is
      // followed by a clause boundary, NOT a command verb:
      //   - a particle marker   (SOV `the घटना को …`  → reference="event")
      //   - a sequence/conjunction keyword or end-of-stream (SVO `the evento
      //     entonces …`, `the подія тоді …` → reference="event")
      // It deliberately does NOT fire for `the <ref-noun> <verb>` (tr
      // form-submit-prevent's `the olay çağır …` = "the event call …", ref noun
      // followed by the `call` verb): skipping `the` there breaks tr's fragile
      // body parse (the §7y regression). Before the boundary cases were added, the
      // SVO/VSO translations kept the leaked `the` as the patient
      // (`patient:expression="the"`), mismatching both the en reference and the SOV
      // parse — part of the `halt.patient` R1 residue. See STRUCTURAL_ARCS_ROADMAP.md
      // (hi halt-propagation).
      const afterRefNoun = tokens.peek(1);
      const afterRefNorm = afterRefNoun
        ? (afterRefNoun.normalized ?? afterRefNoun.value).toLowerCase()
        : '';
      // In VERB-FIRST word orders (SVO/VSO/V2) a ref-noun followed by a
      // COMMAND VERB is also a clause boundary: the verb begins the NEXT
      // juxtaposed body command (`detener the evento llamar validateForm()…`
      // — evento ends the halt clause, llamar opens the call). Without it the
      // patient captured the bare article (`patient:expression="the"`, the
      // halt.patient R1 residue across es/it/de/pl/zh + the behaviors ×17).
      // Deliberately NOT applied to SOV profiles: there the fronted patient's
      // own verb comes LATER in the clause, so a following verb is NOT a
      // boundary (tr `the olay çağır …` — the §7y regression this branch's
      // gate originally protected).
      const verbBoundary =
        this.currentProfile?.wordOrder !== 'SOV' &&
        PatternMatcher.COMMAND_ACTION_KEYWORDS.has(afterRefNorm);
      const refNounFollowedByBoundary =
        !afterRefNoun ||
        afterRefNoun.kind === 'particle' ||
        PatternMatcher.CLAUSE_BOUNDARY_KEYWORDS.has(afterRefNorm) ||
        verbBoundary;
      if (
        nextToken &&
        nextToken.kind === 'keyword' &&
        this.currentProfile?.code !== 'en' &&
        isValidReference(nextNorm) &&
        refNounFollowedByBoundary
      ) {
        return;
      }

      // English authored `the` before a reference word ("halt the event"). The
      // reference (`event`, `result`, `target`, …) tokenizes as a `keyword`, so
      // the selector/identifier skip (case 1) and the positional skip (case 2)
      // both miss it, and the role would otherwise capture only the article
      // (`halt the event` → patient:literal="the", dropping the event). Skip it so
      // the canonical reference fills the role (patient:reference="event"). This
      // matches what the SOV branch above already produces for the leaked-article
      // translations (`the घटना को` → reference="event"); before this, en was the
      // odd reference (literal="the") that every faithful SOV parse mismatched —
      // the dominant `halt.patient` R1 residue. en-only and gated to a valid
      // reference: an English noun ("the color") is an identifier (case 1) and a
      // non-reference keyword ("the default") is left untouched.
      if (
        nextToken &&
        nextToken.kind === 'keyword' &&
        this.currentProfile?.code === 'en' &&
        isValidReference(nextNorm)
      ) {
        return;
      }

      // Not followed by a selector, identifier, or positional keyword — revert
      tokens.reset(mark);
    }

    // Also handle "class" after class selectors: ".visible class" → ".visible"
    // This is handled when the selector has already been consumed,
    // so we check if current token is "class" and skip it
    if (tokenLower === 'class') {
      // Skip "class" as it's just noise after a class selector
      tokens.advance();
    }
  }

  /**
   * Extract event modifiers from the token stream.
   * Event modifiers are .once, .debounce(N), .throttle(N), .queue(strategy)
   * that can appear after event names.
   *
   * Returns EventModifiers object or undefined if no modifiers found.
   */
  extractEventModifiers(tokens: TokenStream): import('../types').EventModifiers | undefined {
    const modifiers: {
      once?: boolean;
      debounce?: number;
      throttle?: number;
      queue?: 'first' | 'last' | 'all' | 'none';
      from?: SemanticValue;
    } = {};

    let foundModifier = false;

    // Consume all consecutive event modifier tokens
    while (!tokens.isAtEnd()) {
      const token = tokens.peek();
      if (!token || token.kind !== 'event-modifier') {
        break;
      }

      const metadata = token.metadata as
        { modifierName: string; value?: number | string } | undefined;
      if (!metadata) {
        break;
      }

      foundModifier = true;

      switch (metadata.modifierName) {
        case 'once':
          modifiers.once = true;
          break;
        case 'debounce':
          if (typeof metadata.value === 'number') {
            modifiers.debounce = metadata.value;
          }
          break;
        case 'throttle':
          if (typeof metadata.value === 'number') {
            modifiers.throttle = metadata.value;
          }
          break;
        case 'queue':
          if (
            metadata.value === 'first' ||
            metadata.value === 'last' ||
            metadata.value === 'all' ||
            metadata.value === 'none'
          ) {
            modifiers.queue = metadata.value;
          }
          break;
      }

      tokens.advance();
    }

    return foundModifier ? modifiers : undefined;
  }
}

// =============================================================================
// Convenience Functions
// =============================================================================

/**
 * Singleton pattern matcher instance.
 */
export const patternMatcher = new PatternMatcher();

/**
 * Match tokens against a pattern.
 */
export function matchPattern(
  tokens: TokenStream,
  pattern: LanguagePattern
): PatternMatchResult | null {
  return patternMatcher.matchPattern(tokens, pattern);
}

/**
 * Match tokens against multiple patterns, return best match.
 */
export function matchBest(
  tokens: TokenStream,
  patterns: LanguagePattern[]
): PatternMatchResult | null {
  return patternMatcher.matchBest(tokens, patterns);
}
